import { linkedPerson } from "../../../utils/linkedScreeningPerson";
import ScreeningNameNotice from "./ScreeningNameNotice";
import { needsFullName } from "../../../utils/screeningName";
import AdverseMediaArticleRow from "./AdverseMediaArticleRow";
import AdverseMediaSubjectIndicators from "./AdverseMediaSubjectIndicators";
import { highestConcernArticleIndicators } from "../../../utils/adverseMediaSubjectIndicators";
import { enhancedSummaryConcern } from "../../../utils/adverseMediaEnhancedSummary";
import { AdverseMediaLoadingStatus, AdverseMediaPlaceholder } from "./AdverseMediaLoading";
import { articleSelectionLabel, reconcileArticleSelections, ARTICLE_POLICY_VERSION } from "../../../utils/adverseMediaArticlePolicy";
import { nameFieldsForSubject } from "../../../utils/individualName";
/* KycSectionAdverseMediaWithScreening.jsx */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SelectButton } from "primereact/selectbutton";

import Lottie from "lottie-react";

// pick any animations you already have (recommended: reuse the existing ones)
import overallAlertAnim from "../../../images/alert-lottie.json";
import overallClearedAnim from "../../../images/cleared-lottie.json";
import overallPendingAnim from "../../../images/pending-lottie.json";

// ✅ add ONE dedicated adverse-media icon (or reuse one of your existing ones)
import adverseMediaAnim from "../../../images/search-lottie.json"; // create/choose your file
import doubleTapFullIcon from "../../../images/double-tap-full.svg";
import { trackScreeningUsage } from "../../../utils/screeningUsage";
import { MYKYC_API_BASE } from "../../../config/api";
import { auth } from "../../../firebase";

import "../css/KycSectionPepSanctionWithScreening.css";
import "../css/KycSectionAdverseMediaWithScreening.css";

const API_BASE = MYKYC_API_BASE;
const ADVERSE_MEDIA_PATH = "/myKyc/screening/adverse_media_serper";
const ASSESS_PATH = "/myKyc/screening/assess_aml_website";
const SUMMARY_PATH = "/myKyc/screening/adverse_media_ai_summary";

/* -------------------- Helpers -------------------- */

function OverallSummaryLine({ line }) {
  // Format the generated overall sentence for display without changing its rating.
  const text = String(line ?? "");
  const match = text.match(/^\s*Overall:\s*(\d+)\s+subjects?\s+shows?\s+(?:elevated|high)\s+concern:\s*(.+?)\s*$/i);
  const noSubjects = /^\s*Overall:\s*no subjects show elevated concern\.?\s*$/i.test(text);
  if (!match && !noSubjects) return line;
  const singular = match && Number(match[1]) === 1;
  const names = match ? match[2].replace(/\.$/, "").trim() : "None";
  return <>
    <span className="kycAdverseSummaryOverallLabel">{singular ? "Subject" : "Subjects"} with elevated concern:</span>
    <span className="kycAdverseSummaryOverallText">{names}</span>
  </>;
}

function SummaryFinding({ line, subjects }) {
  const text = String(line ?? "");
  const names = [...new Set(subjects.map(subject => String(subject.name || "").trim()).filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!names.length) return text;
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])(${names.join("|")})(?=$|[^\\p{L}\\p{N}])`, "giu");
  const parts = [];
  let offset = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index + match[1].length;
    parts.push(text.slice(offset, start), <strong key={start}>{match[2]}</strong>);
    offset = start + match[2].length;
  }
  parts.push(text.slice(offset));
  return <>{parts}</>;
}

function clean(v) {
  return String(v ?? "")
    .replace(/\uFEFF/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^"(.*)"$/, "$1");
}

function buildScreeningUsageContext(form, screeningType) {
  return {
    orgId: clean(form?.orgId || form?.customer?.orgId || ""),
    clientId: clean(form?.clientId || form?.id || form?.customer?.clientId || ""),
    screeningType,
    source: "kyc",
  };
}

function normKey(s) {
  return clean(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
}

function clamp100(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(100, Math.round(x)));
}

function concernLabelFromIndicators(ind) {
  const v = clamp100(ind?.overallConcern);

  // Your rule (keep it simple and deterministic):
  // - high concern if overallConcern is very high
  // - elevated concern if above your elevated threshold
  // - otherwise no material concern
  if (v >= 75) return "high concern";
  if (v >= 50) return "elevated concern";
  return "no material concern identified";
}

function subjectConcernBadge(indicators) {
  const tone = indicators.concern === "high" ? "danger"
    : indicators.concern === "medium" ? "warning"
    : indicators.concern === "low" ? "success" : "grey";
  return badge(indicators.concernLabel, tone);
}

function confirmedHitsLabel(n) { return articleSelectionLabel(n); }

function titleCaseName(name) {
  const s = clean(name);
  if (!s) return "";
  return s
    .split(/\s+/)
    .map((w) => {
      const lower = w.toLowerCase();
      const particles = new Set([
        "de",
        "du",
        "des",
        "la",
        "le",
        "van",
        "von",
        "da",
        "di",
        "del",
      ]);
      if (particles.has(lower)) return lower;

      return lower
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-");
    })
    .join(" ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function badge(text, scheme = "grey") {
  const cls =
    scheme === "danger"
      ? "badge badgeStandard badgeKycRed"
      : scheme === "warning"
      ? "badge badgeStandard badgeKycYellow"
      : scheme === "success"
      ? "badge badgeStandard badgeKycGreen"
      : scheme === "orange"
      ? "badge badgeStandard badgeKycOrange"
      : scheme === "info"
      ? "badge badgeStandard badgeKycBlueLight"
      : "badge badgeStandard badgeKycGrey";
  return <span className={cls}>{text}</span>;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function apiFetch(
  path,
  {
    method = "POST",
    token,
    body,
    timeoutMs = 60000,
    retries = 0,
    retryDelayMs = 800,
  } = {}
) {
  const url = `${API_BASE}${path}`;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // ✅ Only set JSON headers when sending a JSON body
      // (avoids unnecessary preflights, especially for GET)
      const hasBody = body !== undefined && body !== null && method !== "GET";
      if (hasBody) headers["Content-Type"] = "application/json";

      const res = await fetch(url, {
        method,
        headers,
        body: hasBody ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      const text = await res.text();
      let json = {};
      try {
        json = text ? JSON.parse(text) : {};
      } catch {}

      if (!res.ok || json?.ok === false) {
        const msg = json?.error || json?.message || text || "Request failed";
        const err = new Error(msg);
        err.httpStatus = res.status;

        // ✅ retry on typical transient errors
        const retryable =
          res.status === 429 ||
          res.status === 502 ||
          res.status === 503 ||
          res.status === 504;

        if (attempt < retries && retryable) {
          await sleep(retryDelayMs * (attempt + 1));
          continue;
        }

        throw err;
      }

      return json;
    } catch (err) {
      const aborted = err?.name === "AbortError";
      const retryable =
        aborted ||
        err?.httpStatus === 429 ||
        err?.httpStatus === 502 ||
        err?.httpStatus === 503 ||
        err?.httpStatus === 504;

      if (attempt < retries && retryable) {
        await sleep(retryDelayMs * (attempt + 1));
        continue;
      }

      console.error("apiFetch failed:", { url, err });
      throw err;
    } finally {
      clearTimeout(t);
    }
  }
}

async function apiFetch_5march1601(
  path,
  { method = "POST", token, body } = {}
) {
  const url = `${API_BASE}${path}`;
  try {
    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const text = await res.text();
    let json = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch {}

    if (!res.ok || json?.ok === false) {
      console.error("apiFetch HTTP failed:", {
        url,
        status: res.status,
        text,
        json,
      });
      throw new Error(json?.error || json?.message || text || "Request failed");
    }

    return json;
  } catch (err) {
    // ✅ THIS is where "Failed to fetch" originates
    console.error("apiFetch NETWORK failed:", { url, err });
    throw err; // keep original "Failed to fetch"
  }
}

async function apiFetch_OLD(path, { method = "POST", token, body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let json = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    // ignore
  }

  if (!res.ok || json?.ok === false) {
    console.error("apiFetch failed:", {
      path,
      status: res.status,
      sentBody: body,
      raw: text,
      parsed: json,
    });
    const msg = json?.error || json?.message || text || "Request failed";
    throw new Error(msg);
  }

  return json;
}

function isHttpUrl(url) {
  return /^https?:\/\//i.test(String(url || ""));
}

function getHostname(url) {
  try {
    return new URL(String(url || "")).hostname
      .toLowerCase()
      .replace(/^www\./, "");
  } catch {
    return "";
  }
}

function looksLikeNonHtmlUrl(url) {
  const u = String(url || "");
  if (!u) return true;

  if (/\.(pdf|ppt|pptx|doc|docx|xls|xlsx|csv|zip|rar|7z)(\b|$)/i.test(u))
    return true;
  if (/\/TXT\/PDF/i.test(u)) return true;
  if (/\/cgi\/viewcontent\.cgi\b/i.test(u)) return true;

  if (/[?&](format|type|output|view)=pdf\b/i.test(u)) return true;
  if (/[?&]download=1\b/i.test(u)) return true;
  if (/[?&]filetype=pdf\b/i.test(u)) return true;

  return false;
}

const HARD_BLOCK_DOMAINS = new Set([
  "facebook.com",
  "m.facebook.com",
  "tiktok.com",
  "www.tiktok.com",
]);

function isHardBlockedDomain(url) {
  const d = getHostname(url);
  if (!d) return false;
  if (HARD_BLOCK_DOMAINS.has(d)) return true;
  for (const root of HARD_BLOCK_DOMAINS) {
    if (d.endsWith(`.${root}`)) return true;
  }
  return false;
}

function keepRowByUrl(r) {
  const url = clean(r?.link);
  if (!url) return false;
  if (!isHttpUrl(url)) return false;
  if (looksLikeNonHtmlUrl(url)) return false;
  if (isHardBlockedDomain(url)) return false;
  return true;
}

function isEmptyHtmlAssessment(a) {
  const err = clean(a?.error || "");
  if (!err) return false;
  const e = err.toLowerCase();
  return (
    e.includes("empty page text") ||
    e.includes("empty html page text") ||
    e.includes("serper scrape returned empty text") ||
    e.includes("rejected: pdf") ||
    e.includes("non-html") ||
    e.includes("content-type application/pdf") ||
    e.includes("content type application/pdf") ||
    e.includes("content-type is application/pdf") ||
    e.includes("application/pdf") ||
    e.includes("application-pdf") ||
    e.includes("content-type is application-pdf") ||
    e.includes("content-type is application pdf")
  );
}

function getLastName(fullName) {
  const parts = clean(fullName).split(" ").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

function upper2(v) {
  const x = clean(v).toUpperCase();
  return x && x.length === 2 ? x : "";
}

// Try to infer whether a subject is an entity vs individual.
// (We'll still override later if you already store an explicit type field.)
function guessSubjectTypeFromName(name) {
  const n = clean(name).toLowerCase();
  if (!n) return "individual";

  // very light heuristic: if it contains common legal suffix tokens => entity
  const tokens = n.split(/\s+/);
  const entityHints = new Set([
    "llc",
    "l.l.c",
    "inc",
    "inc.",
    "ltd",
    "ltd.",
    "limited",
    "gmbh",
    "ag",
    "sa",
    "s.a",
    "s.a.",
    "sarl",
    "sàrl",
    "bv",
    "nv",
    "plc",
    "pte",
    "llp",
    "lp",
    "dmcc",
    "fzco",
    "fzc",
    "pjsc",
    "jsc",
  ]);

  if (tokens.some((t) => entityHints.has(t))) return "entity";
  return "individual";
}

// Read country context from your form data.
// You said these are already stored in Firestore KYC form; we just extract them.
// If some fields are missing, we fall back gracefully.
function getCountryContextFromForm(form, subjectName, subjectType) {
  const type = (subjectType || "individual").toLowerCase();

  // 1) Client (customer)
  const customer = form?.customer || {};
  const customerName = clean(customer?.name);

  if (customerName && clean(subjectName) === customerName) {
    const customerType = clean(customer?.type).toLowerCase(); // "individual" | "entity" (your schema)

    // In your form:
    // - nationality for individuals OR incorporation country for entities is stored as:
    //   customer.nationalityOrIncorporationCountry (ISO2)
    // - domicile/residence country is stored as:
    //   customer.country (ISO2)
    const natOrIncorp = upper2(customer?.nationalityOrIncorporationCountry);
    const domicile = upper2(customer?.country);

    if (
      customerType === "entity" ||
      clean(subjectType).toLowerCase() === "entity"
    ) {
      return {
        nationality: "",
        domicileCountry: "",
        incorpCountry: natOrIncorp, // entity: use incorp
      };
    }

    // individual
    return {
      nationality: natOrIncorp,
      domicileCountry: domicile,
      incorpCountry: "",
    };
  }

  // 2) Ownership parties: shareholders / UBOs (try to match by name)
  const ownership = form?.ownership || {};
  const shareholders = Array.isArray(ownership?.shareholders)
    ? ownership.shareholders
    : Array.isArray(ownership?.directShareholders)
    ? ownership.directShareholders
    : [];
  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];

  const allParties = [...shareholders, ...ubos];

  const nm = clean(subjectName);
  const party = allParties.find((p) => {
    const n =
      clean(p?.name) ||
      clean(p?.fullName) ||
      clean(p?.legalName) ||
      clean(p?.displayName) ||
      clean(
        [p?.firstName, p?.middleName, p?.lastName].filter(Boolean).join(" ")
      );
    return n && clean(n) === nm;
  });

  // If we found the matching party, pull the right fields depending on type
  if (party) {
    if (type === "entity") {
      return {
        nationality: "",
        domicileCountry: "",
        incorpCountry: upper2(
          party?.incorpCountry ||
            party?.incorporationCountry ||
            party?.countryOfIncorporation ||
            party?.registeredCountry
        ),
      };
    }

    // individual
    return {
      nationality: upper2(party?.nationality || party?.countryNationality),
      domicileCountry: upper2(
        party?.domicileCountry ||
          party?.countryOfResidence ||
          party?.residenceCountry
      ),
      incorpCountry: "",
    };
  }

  // 3) fallback: unknown
  return { nationality: "", domicileCountry: "", incorpCountry: "" };
}

/*  --------  */

/* -------------------- ✅ KEY NORMALIZATION -------------------- */

function subjectKeyFromSubject(s) {
  const k = clean(s?.key);
  const n = clean(s?.name);
  return normKey(k || n);
}

function subjectKeyFromRow(r) {
  const k = clean(r?.subjectKey);
  const n = clean(r?.subjectName);
  return normKey(k || n);
}

/* -------------------- Subject bullet grouping -------------------- */

function toBulletSentence(s) {
  const t = clean(s || "");
  if (!t) return "";
  return t.replace(/^the article\b/i, "An article");
}

function accusedIncludesSubject({ accusedParties, subjectName }) {
  const accused = Array.isArray(accusedParties)
    ? accusedParties.map(clean).filter(Boolean)
    : [];
  const subj = clean(subjectName);
  if (!subj || !accused.length) return false;

  const normalize = value => clean(value).normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  const full = normalize(subj);
  return !!full && accused.some(value => normalize(value) === full);
}

function computeGroupedBulletsForSubject(allRowsForSubject, liveAssessments) {
  const rows = Array.isArray(allRowsForSubject) ? allRowsForSubject : [];

  // ✅ include ONLY successful AI assessments (ok === true)
  // ✅ BUT do NOT require accusedParties to exist
  const okRows = rows
    .map((r) => {
      const a = liveAssessments?.[String(r?.id)];
      return a?.ok === true ? { r, a } : null; // only ok assessments
    })
    .filter(Boolean)
    .sort(
      (x, y) => (Number(y?.r?.rankScore) || 0) - (Number(x?.r?.rankScore) || 0)
    );

  const targetedMap = new Map();
  const contextualMap = new Map();

  for (const x of okRows) {
    const rowId = String(x?.r?.id || "");
    const subjName = clean(x?.r?.subjectName);

    const text = toBulletSentence(x?.a?.assessment?.concernsSummary);
    if (!text) continue;

    const accused = Array.isArray(x?.a?.assessment?.accusedParties)
      ? x.a.assessment.accusedParties.map(clean).filter(Boolean)
      : [];

    // ✅ if no accused parties detected => treat as contextual
    const isTargeted = x.a.assessment.assessmentVersion === ARTICLE_POLICY_VERSION
      ? x.a.assessment.involvement === "direct"
      : accused.length > 0
        ? accusedIncludesSubject({
            accusedParties: accused,
            subjectName: subjName,
          })
        : false;

    const key = text.toLowerCase();
    const map = isTargeted ? targetedMap : contextualMap;
    const prev = map.get(key);

    if (!prev) {
      map.set(key, { text, rowIds: rowId ? [rowId] : [] });
    } else if (rowId && !prev.rowIds.includes(rowId)) {
      prev.rowIds.push(rowId);
    }
  }

  return {
    targeted: Array.from(targetedMap.values()).slice(0, 5),
    notTargeted: Array.from(contextualMap.values()).slice(0, 5),
  };
}

// function computeGroupedBulletsForSubject(allRowsForSubject, liveAssessments) {
//   const rows = Array.isArray(allRowsForSubject) ? allRowsForSubject : [];

//   const okRows = rows
//     .map((r) => {
//       const a = liveAssessments?.[String(r?.id)];
//       return a?.ok ? { r, a } : null;
//     })
//     .filter(Boolean)
//     .sort(
//       (x, y) => (Number(y?.r?.rankScore) || 0) - (Number(x?.r?.rankScore) || 0)
//     )
//     .slice(0, 10);

//   const targetedMap = new Map();
//   const contextualMap = new Map();

//   for (const x of okRows) {
//     const rowId = String(x?.r?.id || "");
//     const subjName = clean(x?.r?.subjectName);

//     const accused = Array.isArray(x?.a?.assessment?.accusedParties)
//       ? x.a.assessment.accusedParties.map(clean).filter(Boolean)
//       : [];

//     if (!accused.length) continue;

//     const text = toBulletSentence(x?.a?.assessment?.concernsSummary);
//     if (!text) continue;

//     const isTargeted = accusedIncludesSubject({
//       accusedParties: accused,
//       subjectName: subjName,
//     });

//     const key = text.toLowerCase();
//     const map = isTargeted ? targetedMap : contextualMap;
//     const prev = map.get(key);

//     if (!prev) {
//       map.set(key, { text, rowIds: rowId ? [rowId] : [] });
//     } else if (rowId && !prev.rowIds.includes(rowId)) {
//       prev.rowIds.push(rowId);
//     }
//   }

//   return {
//     targeted: Array.from(targetedMap.values()).slice(0, 5),
//     notTargeted: Array.from(contextualMap.values()).slice(0, 5),
//   };
// }

/* -------------------- Source quality label -------------------- */

function sourceQualityLabelFromScore(sourceQualityScore) {
  const s = clamp100(sourceQualityScore);
  if (s >= 75) return "High";
  if (s >= 55) return "Medium";
  return "Low";
}

/* -------------------- AML-ish terms -------------------- */

const AMLISH_TERMS = [
  "money laundering",
  "laundering",
  "aml",
  "anti-money laundering",
  "suspicious transaction",
  "proceeds of crime",
  "fraud",
  "embezzlement",
  "forgery",
  "scam",
  "corruption",
  "bribery",
  "kickback",
  "sanctions",
  "ofac",
  "blacklist",
  "asset freeze",
  "tax evasion",
  "financial crime",
  "investigation",
  "charged",
  "indicted",
  "convicted",
  "sentenced",
  "arrested",
  "court",
  "prosecutor",
  "regulator",
  "fine",
  "fined",
  "penalty",
  "enforcement action",
];

function countTermsPresent(text) {
  const t = String(text || "").toLowerCase();
  let c = 0;
  for (const term of AMLISH_TERMS) {
    if (!term) continue;
    if (t.includes(term)) c += 1;
  }
  return c;
}

function keywordSecondaryPointsFromAssessment(assessment) {
  const concerns = clean(assessment?.concernsSummary || "");
  const summaryArr = Array.isArray(assessment?.textSummary)
    ? assessment.textSummary
    : [];
  const text = `${concerns} ${summaryArr.map(clean).join(" ")}`.trim();
  const hits = countTermsPresent(text);
  return Math.min(10, hits);
}

/* -------------------- Match strength -------------------- */

function subjectNameInTitleOrSnippet({ subjectName, title, snippet }) {
  const subj = clean(subjectName);
  if (!subj) return false;

  const full = subj.toLowerCase();
  const last = getLastName(subj).toLowerCase();

  const t = clean(title).toLowerCase();
  const s = clean(snippet).toLowerCase();

  if (full.includes(" ") && (t.includes(full) || s.includes(full))) return true;
  if (last && (t.includes(last) || s.includes(last))) return true;

  return false;
}

function matchStrength({ isTargeted, nameInTitleSnippet }) {
  if (isTargeted) return 100;
  if (nameInTitleSnippet) return 60;
  return 10;
}

function strengthFromNameMatch(nameMatch) {
  const v = clean(nameMatch).toLowerCase();
  if (v === "exact") return 100;
  if (v === "close") return 70;
  if (v === "weak") return 30;
  return 10;
}

function geographyBonusFromMatch(geo) {
  const v = clean(geo).toLowerCase();
  if (v === "supports") return 10;
  if (v === "conflicts") return -10;
  return 0;
}

function computeRelatednessStrength({
  row,
  assessmentObj,
  fallbackIsTargeted,
  fallbackNameInTS,
}) {
  // ✅ NEW: use backend score directly
  const score = Number(assessmentObj?.assessment?.nameMatchScore);
  if (Number.isFinite(score)) return clamp100(score);

  // fallback to previous logic
  const nm = clean(assessmentObj?.assessment?.nameMatch);
  if (nm) {
    const base = strengthFromNameMatch(nm);
    const geo = geographyBonusFromMatch(
      assessmentObj?.assessment?.geographyMatch
    );
    return clamp100(base + geo);
  }

  return matchStrength({
    isTargeted: !!fallbackIsTargeted,
    nameInTitleSnippet: !!fallbackNameInTS,
  });
}

function computeRelatednessStrength_OLD({
  row,
  assessmentObj,
  fallbackIsTargeted,
  fallbackNameInTS,
}) {
  // Prefer server-provided nameMatch
  const nm = clean(assessmentObj?.assessment?.nameMatch);
  if (nm) {
    const base = strengthFromNameMatch(nm);
    const geo = geographyBonusFromMatch(
      assessmentObj?.assessment?.geographyMatch
    );
    return clamp100(base + geo);
  }

  // Fallback to old heuristics
  return matchStrength({
    isTargeted: !!fallbackIsTargeted,
    nameInTitleSnippet: !!fallbackNameInTS,
  });
}

/* -------------------- Relevance score -------------------- */

function sourceQualityPointsFromScore(sourceQualityScore) {
  const label = sourceQualityLabelFromScore(sourceQualityScore);
  const L = String(label || "").toLowerCase();
  if (L === "high") return 20;
  if (L === "medium") return 15;
  return 5;
}

function computeOverallRelevance({
  amlSeverity,
  sourceQualityScore,
  nameMatchScore,
  isTargeted,
}) {
  // ✅ same base logic as subject indicators (rowConcern)
  const base = computeOverallConcernForRow({
    amlSeverity,
    sourceQualityScore,
    nameMatchScore,
  });

  // ✅ optional boost if subject is directly implicated by accusedParties detection
  const boost = isTargeted ? 10 : 0;

  return clamp100(base + boost);
}

// function computeOverallRelevance({ row, assessment, isTargeted }) {
//   const accusedPoints = isTargeted ? 40 : 0;
//   const srcPoints = sourceQualityPointsFromScore(row?.sourceQualityScore);

//   const kwMainPoints = Math.round(
//     (clamp100(row?.keywordSignalScore) / 100) * 10
//   );

//   const kwSecondaryPoints = assessment?.ok
//     ? keywordSecondaryPointsFromAssessment(assessment?.assessment)
//     : 0;

//   const aiRisk = assessment?.ok
//     ? clamp100(assessment?.assessment?.riskScore)
//     : 0;
//   const aiPoints = Math.round((aiRisk / 100) * 20);

//   return clamp100(
//     accusedPoints + srcPoints + kwMainPoints + kwSecondaryPoints + aiPoints
//   );
// }

function computeOverallConcernForRow({
  amlSeverity,
  sourceQualityScore,
  nameMatchScore,
}) {
  const sev = clamp100(amlSeverity);
  const src = clamp100(sourceQualityScore);
  const nm = clamp100(nameMatchScore);

  // Example weighting (tweak anytime)
  // severity is main, but down-weight if weak name match or weak source
  return clamp100(0.55 * sev + 0.25 * src + 0.2 * nm);
}

/* -------------------- Subject indicators -------------------- */

function credibilityFactorFromSourceScore(sourceQualityScore) {
  const s = clamp100(sourceQualityScore);
  if (s >= 75) return 1.0;
  if (s >= 55) return 0.75;
  return 0.4;
}

function identityFactorFromStrength(strength) {
  const s = clamp100(strength);
  if (s >= 100) return 1.0;
  if (s >= 60) return 0.5;
  return 0.15;
}

function computeRowConcernScore({ riskScore, sourceQualityScore, strength }) {
  const severity = clamp100(riskScore);
  const cred = credibilityFactorFromSourceScore(sourceQualityScore);
  const ident = identityFactorFromStrength(strength);
  return clamp100(severity * cred * ident);
}

// Legacy summary inputs remain until the separate AI-summary stage is updated.
function computeSummaryIndicators({ allRowsForSubject, liveAssessments }) {
  const totalHits = (allRowsForSubject || []).length;

  const usable = [];
  let failed = 0; // ✅ only failures count as "Excluded"

  for (const r of allRowsForSubject || []) {
    const rowId = String(r?.id || "");
    const a = liveAssessments?.[rowId];

    // ✅ not assessed yet -> NOT excluded
    if (!a) continue;

    // ✅ AI assessment failed -> excluded
    if (a?.ok === false) {
      failed += 1;
      continue;
    }

    // ✅ AI assessment ok -> usable (even if accusedParties is empty)
    if (a?.ok === true) {
      usable.push({ r, a });
    }
  }

  if (!usable.length) {
    return {
      overallConcern: 0,
      relatedness: 0,
      sourceQuality: 0,
      usableCount: 0,
      excludedCount: failed, // ✅ only failed
      totalHits,
      dominant: null,
    };
  }

  const scored = usable
    .map(({ r, a }) => {
      const subjName = clean(r?.subjectName);

      const accused = Array.isArray(a?.assessment?.accusedParties)
        ? a.assessment.accusedParties.filter(Boolean)
        : [];

      const targeted = a.assessment.assessmentVersion === ARTICLE_POLICY_VERSION
        ? a.assessment.involvement === "direct"
        : accused.length > 0
          ? accusedIncludesSubject({
              accusedParties: accused,
              subjectName: subjName,
            })
          : false;

      const nameInTS = subjectNameInTitleOrSnippet({
        subjectName: subjName,
        title: r?.title,
        snippet: r?.snippet,
      });

      const strength = computeRelatednessStrength({
        row: r,
        assessmentObj: a,
        fallbackIsTargeted: targeted,
        fallbackNameInTS: nameInTS,
      });

      const riskScore = a?.assessment?.riskScore;

      // Blend “severity” (AI) + “relevance”
      // const severity = clamp100(a?.assessment?.riskScore);
      // const blended = clamp100(0.55 * relevance + 0.45 * severity);

      // const rowConcern = computeRowConcernScore({
      //   riskScore: blended, // <-- use blended score
      //   sourceQualityScore: r?.sourceQualityScore,
      //   strength,
      // });

      const amlSeverity = clamp100(a?.assessment?.amlSeverityScore);

      const rowConcern = computeOverallConcernForRow({
        amlSeverity,
        sourceQualityScore: r?.sourceQualityScore,
        nameMatchScore: strength, // your name match confidence
      });

      // const rowConcern = computeRowConcernScore({
      //   riskScore,
      //   sourceQualityScore: r?.sourceQualityScore,
      //   strength,
      // });

      return { r, a, targeted, strength, rowConcern };
    })
    .sort((x, y) => (y.rowConcern || 0) - (x.rowConcern || 0));

  const dominant = scored[0] || null;

  const top1 = clamp100(scored[0]?.rowConcern || 0);
  const top2 = clamp100(scored[1]?.rowConcern || 0);
  // const overallConcern =
  //   scored.length >= 2 ? clamp100((top1 + top2) / 2) : clamp100(top1);
  const overallConcern =
    scored.length >= 2 ? clamp100(0.75 * top1 + 0.25 * top2) : clamp100(top1);

  const relatedness = clamp100(dominant?.strength || 0);

  const top3 = scored.slice(0, 3);

  const amlAvg =
    top3.reduce(
      (sum, x) => sum + clamp100(x?.a?.assessment?.amlSeverityScore),
      0
    ) / Math.max(1, top3.length);

  const amlSeverity = clamp100(amlAvg);

  const srcAvg =
    top3.reduce((sum, x) => sum + clamp100(x?.r?.sourceQualityScore), 0) /
    Math.max(1, top3.length);

  const sourceQuality = clamp100(srcAvg);

  return {
    overallConcern,
    amlSeverity,
    relatedness,
    sourceQuality,
    usableCount: usable.length, // ✅ ok assessments (even if no accused)
    excludedCount: failed, // ✅ ONLY failed assessments
    totalHits,
    dominant,
  };
}

/* -------------------- Per-subject processing message and progress -------------------- */

function SubjectLoader({
  stage,
  done = 0,
  total = 0,
  searchingLabel = "Searching sources…",
  assessingLabel = "Assessing…",
  waitingLabel = "Waiting for AI assessment…",
}) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeDone = Math.max(0, Number(done) || 0);
  const pct = safeTotal > 0 ? Math.round((safeDone / safeTotal) * 100) : 0;

  const trackRef = React.useRef(null);
  const barRef = React.useRef(null);

  const [searchBarVisuallyFull, setSearchBarVisuallyFull] =
    React.useState(false);

  // Reset whenever we aren't in the exact situation we care about
  React.useEffect(() => {
    if (stage !== "searching" || pct < 100) setSearchBarVisuallyFull(false);
  }, [stage, pct]);

  // ✅ When pct hits 100 in searching, wait until the bar is actually visually full
  React.useEffect(() => {
    if (stage !== "searching" || pct < 100) return;

    let raf = 0;
    const start = performance.now();
    const MAX_MS = 800; // safety window
    const EPS = 1.5; // px tolerance

    const tick = () => {
      const track = trackRef.current;
      const bar = barRef.current;

      if (track && bar) {
        const trackW = track.getBoundingClientRect().width;
        const barW = bar.getBoundingClientRect().width;

        if (trackW > 0 && barW >= trackW - EPS) {
          setSearchBarVisuallyFull(true);
          return;
        }
      }

      if (performance.now() - start > MAX_MS) {
        // if we can't measure perfectly, still switch after a short grace period
        setSearchBarVisuallyFull(true);
        return;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stage, pct]);

  const label =
    stage === "searching"
      ? pct >= 100 && searchBarVisuallyFull
        ? waitingLabel
        : searchingLabel
      : stage === "assessing"
      ? safeTotal > 0
        ? assessingLabel
        : waitingLabel
      : searchingLabel;

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center gap-2 text-muted small">
        <span>{label}</span>
      </div>

      <div style={{ width: 220, maxWidth: "100%" }}>
        <div
          ref={trackRef}
          className="progress mt-2 kycAssessTrack"
          style={{ height: 7, overflow: "hidden" }}
        >
          <motion.div
            ref={barRef}
            className="progress-bar kycAssessBar"
            style={{ borderRadius: 999 }}
            initial={{ width: "0%" }}
            animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            transition={{ type: "spring", stiffness: 140, damping: 22 }}
          />
        </div>
      </div>
    </div>
  );
}

// overall progress bar

function OverallProgressBar({
  stage,
  searchDone = 0,
  searchTotal = 0,
  assessDone = 0,
  assessTotal = 0,
  label = "Overall run progress",
}) {
  const sTotal = Math.max(0, Number(searchTotal) || 0);
  const sDone = Math.max(0, Number(searchDone) || 0);
  const aTotal = Math.max(0, Number(assessTotal) || 0);
  const aDone = Math.max(0, Number(assessDone) || 0);

  const sFrac = sTotal > 0 ? Math.min(1, sDone / sTotal) : 0;
  const aFrac = aTotal > 0 ? Math.min(1, aDone / aTotal) : 0;

  // ✅ 0–50 for searching, 50–100 for assessing
  const pct =
    stage === "assessing"
      ? Math.round((0.5 + 0.5 * aFrac) * 100)
      : Math.round(0.5 * sFrac * 100);

  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between small text-muted">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>

      <div
        className="progress mt-1"
        style={{ height: 8, borderRadius: 999, overflow: "hidden" }}
      >
        <motion.div
          className="progress-bar"
          style={{ backgroundColor: "#ABB6C1" }}
          initial={{ width: "0%" }}
          animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          transition={{ type: "spring", stiffness: 140, damping: 22 }}
        />
      </div>
    </div>
  );
}

// ✅ Prevent background scroll while modal is open (locks KycFormPage behind)

// Supports iOS (position: fixed), preserves scroll position, handles nested modals.
function useLockScrollDesktopOnly(locked) {
  React.useEffect(() => {
    if (!locked) return;

    const html = document.documentElement;
    const body = document.body;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
    };

    // ✅ desktop lock
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
    };
  }, [locked]);
}

/* ------------------------------------------------------------ */
/* ------------------------------------------------------------ */
/* -------------------------- Component ----------------------- */
/* ------------------------------------------------------------ */
/* ------------------------------------------------------------ */

const KycSectionAdverseMediaWithScreening = forwardRef(function KycSectionAdverseMediaWithScreening({
  sectionNo = 0,
  form,
  onSetAt,
  setAt,
  changedClass,
  readOnly = false,
  embedded = false,
  hideComplianceAssessmentCard = false,
  complianceAssessmentContent = null,
  initiallyExpanded = false,
  onScreeningStateChange,
}, ref) {
  const writeAt = typeof onSetAt === "function" ? onSetAt : setAt;
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const adverseMedia = form?.adverseMedia || {};
  const internal = form?.internalUse || {};
  const savedScreening = internal?.adverseMediaScreening || {};

  const [language, setLanguage] = useState(adverseMedia?.language || "en");
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [hasRun, setHasRun] = useState(!!savedScreening?.runAt);
  const [assessmentCardReady, setAssessmentCardReady] = useState(
    !!savedScreening?.runAt
  );
  const [completedRunAt, setCompletedRunAt] = useState(
    savedScreening?.completedAt || savedScreening?.runCompletedAt || ""
  );

  const [modalOpen, setModalOpen] = useState(false);
  useLockScrollDesktopOnly(modalOpen);
  const [showDetails, setShowDetails] = useState(!!initiallyExpanded);

  function scrollAdverseAnchorThenCollapse() {
    const anchor = document.getElementById("kyc-sec-adverse");
    anchor?.scrollIntoView({ behavior: "smooth", block: "start" });

    window.setTimeout(() => {
      setShowDetails(false);
    }, 500);
  }

  const [activeSubjectKey, setActiveSubjectKey] = useState("");

  const [localRows, setLocalRows] = useState(
    Array.isArray(savedScreening?.rows) ? savedScreening.rows : []
  );
  const [liveAssessments, setLiveAssessments] = useState(
    savedScreening?.assessmentsByRowId || {}
  );

  const [assessProgress, setAssessProgress] = useState({ done: 0, total: 0 });
  const [assessProgressBySubject, setAssessProgressBySubject] = useState({});
  const [stage, setStage] = useState("idle"); // "idle" | "searching" | "assessing"

  const runTokenRef = useRef(0);
  const runInProgressRef = useRef(false);
  const detailsVisible = showDetails;

  const [overallProgress, setOverallProgress] = useState({
    searchDone: 0,
    searchTotal: 0,
    assessDone: 0,
    assessTotal: 0,
  });

  const [aiSummaryLines, setAiSummaryLines] = useState(
    Array.isArray(savedScreening?.aiSummary?.lines)
      ? savedScreening.aiSummary.lines
      : []
  );
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryError, setAiSummaryError] = useState("");

  const lastSearchTotalRef = useRef(0);
  const lastSearchDoneRef = useRef(0);
  const localRunStartedRef = useRef(false);

  function getSearchTotalsSafe() {
    const total = Number(lastSearchTotalRef.current) || 0;
    const done = Number(lastSearchDoneRef.current) || 0;

    // clamp: done cannot exceed total if total exists
    if (total > 0)
      return { searchTotal: total, searchDone: Math.min(done, total) };

    // if total unknown, treat as 0 (avoid fake progress)
    return { searchTotal: 0, searchDone: 0 };
  }

  // ✅ progress for "searching" stage per subject
  const [searchProgressBySubject, setSearchProgressBySubject] = useState({});

  // ✅ tracks which rowIds are actually scheduled for assessment
  const candidateRowIdsRef = useRef(new Set());

  // Prevent scrolling the background when modal is open

  // -----

  useEffect(() => {
    // Saved form echoes can lag behind the concurrent article workers.
    if (runInProgressRef.current) return;
    if (Array.isArray(savedScreening?.rows)) setLocalRows(savedScreening.rows);
    if (savedScreening?.assessmentsByRowId)
      setLiveAssessments(savedScreening.assessmentsByRowId);
    if (savedScreening?.runAt) setHasRun(true);
    if (savedScreening?.runAt && !localRunStartedRef.current)
      setAssessmentCardReady(true);
    if (savedScreening?.completedAt || savedScreening?.runCompletedAt) {
      setCompletedRunAt(
        savedScreening.completedAt || savedScreening.runCompletedAt
      );
    } else if (savedScreening?.runAt && !localRunStartedRef.current) {
      setCompletedRunAt(savedScreening.runAt);
    }
  }, [
    savedScreening?.rows,
    savedScreening?.assessmentsByRowId,
    savedScreening?.runAt,
    savedScreening?.completedAt,
    savedScreening?.runCompletedAt,
  ]);

  const persistAssessmentsTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(persistAssessmentsTimerRef.current);

    persistAssessmentsTimerRef.current = setTimeout(() => {
      setScreeningField(["assessmentsByRowId"], liveAssessments);
    }, 250);

    return () => clearTimeout(persistAssessmentsTimerRef.current);
    // eslint-disable-next-line
  }, [liveAssessments]);

  const selectedIds = useMemo(() => {
    const arr = Array.isArray(savedScreening?.selectedIds)
      ? savedScreening.selectedIds
      : [];
    return new Set(arr.map(String).filter(id => localRows.some(row => String(row.id) === id)));
  }, [savedScreening?.selectedIds, localRows]);

  const customerName = useMemo(
    () => clean(form?.customer?.name),
    [form?.customer?.name]
  );
  const ownership = form?.ownership || {};
  const isEntityClient = useMemo(() => {
    const t = clean(
      form?.customer?.type ||
        form?.customer?.customerType ||
        form?.customer?.clientType ||
        form?.customer?.profileType ||
        form?.customer?.personType ||
        form?.customer?.category ||
        ""
    ).toLowerCase();

    if (
      t.includes("entity") ||
      t.includes("company") ||
      t.includes("corporate")
    )
      return true;
    if (t.includes("individual") || t.includes("person")) return false;
    if (form?.customer?.isEntity === true) return true;

    const hasOwnershipNames =
      (Array.isArray(ownership?.shareholders) &&
        ownership.shareholders.some((x) => clean(x?.name))) ||
      (Array.isArray(ownership?.ubos) &&
        ownership.ubos.some((x) => clean(x?.name))) ||
      (Array.isArray(ownership?.signatories) &&
        ownership.signatories.some((x) => clean(x?.name))) ||
      (Array.isArray(ownership?.authorisedSignatories) &&
        ownership.authorisedSignatories.some((x) => clean(x?.name))) ||
      (Array.isArray(ownership?.directors) &&
        ownership.directors.some((x) => clean(x?.name)));

    return !!hasOwnershipNames;
  }, [form?.customer, ownership]);

  const allScreeningSubjects = useMemo(() => {
    const map = new Map();
    const accountHolderIsPicOrPassive = !!ownership?.uboFlags?.pctNotMeaningful;
    const legalArrangements = Array.isArray(ownership?.legalArrangements?.arrangements)
      ? ownership.legalArrangements.arrangements
      : [];

    const pickName = (obj) => {
      if (!obj || typeof obj !== "object") return "";
      return (
        obj.name ||
        obj.fullName ||
        obj.legalName ||
        obj.displayName ||
        [obj.firstName, obj.middleName, obj.lastName]
          .filter(Boolean)
          .join(" ") ||
        ""
      );
    };

    const upsert = (name, role, meta = {}) => {
      const nm = clean(name);
      if (!nm) return;
      const key = normKey(nm);

      const subjectType =
        clean(meta?.subjectType).toLowerCase() === "entity"
          ? "entity"
          : clean(meta?.subjectType).toLowerCase() === "individual"
          ? "individual"
          : guessSubjectTypeFromName(nm);

      const countryContext =
        typeof meta?.countryContext === "object" && meta.countryContext
          ? meta.countryContext
          : getCountryContextFromForm(form, nm, subjectType);

      const prev = map.get(key);

      if (!prev) {
        map.set(key, {
          key,
          name: nm,
          roles: [role],
          subjectType,
          countryContext,
        });
        return;
      }

      if (!prev.roles.includes(role)) prev.roles.push(role);

      // keep stable values if already set; else fill
      if (!prev.subjectType) prev.subjectType = subjectType;
      if (!prev.countryContext) prev.countryContext = countryContext;
    };

    if (customerName)
      upsert(customerName, "Client", {
        subjectType: isEntityClient ? "entity" : "individual",
        countryContext: getCountryContextFromForm(
          form,
          customerName,
          isEntityClient ? "entity" : "individual"
        ),
      });

    const directShareholders = Array.isArray(ownership?.shareholders)
      ? ownership.shareholders
      : Array.isArray(ownership?.directShareholders)
      ? ownership.directShareholders
      : [];

    const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];

    const getPct = (obj) => {
      const candidates = [
        obj?.ownershipPercent,
        obj?.pct,
        obj?.percentage,
        obj?.ownershipPct,
        obj?.ownership,
        obj?.sharePct,
        obj?.shareholdingPct,
      ];

      for (const v of candidates) {
        if (v == null) continue;
        const n =
          typeof v === "string" ? Number(v.trim().replace("%", "")) : Number(v);
        if (Number.isFinite(n)) return n;
      }
      return null; // no percentage indicated
    };

    for (const r of directShareholders) {
      const nm = pickName(r);

      // try to infer entity/individual from fields if present (else fallback heuristic)
      const inferredType =
        ["entity", "company", "trust", "foundation"].includes(String(r?.holderType || "").toLowerCase()) ? "entity"
        : String(r?.holderType || "").toLowerCase() === "individual" ? "individual"
        : String(r?.partyType || r?.entityType || r?.type || "")
          .toLowerCase()
          .includes("entity") ||
        String(r?.partyType || "")
          .toLowerCase()
          .includes("company")
          ? "entity"
          : String(r?.partyType || r?.entityType || "")
              .toLowerCase()
              .includes("individual")
          ? "individual"
          : guessSubjectTypeFromName(nm);

      const pct = getPct(r);
      const shouldScreen =
        (pct != null && pct >= 50) ||
        (accountHolderIsPicOrPassive && inferredType === "individual");

      if (!shouldScreen) continue;

      upsert(nm, "Direct shareholder", {
        subjectType: inferredType,
        countryContext: getCountryContextFromForm(form, nm, inferredType),
      });
    }

    for (const u of ubos) {
      const nm = pickName(u);
      upsert(nm, "UBO", {
        subjectType: "individual",
        countryContext: getCountryContextFromForm(form, nm, "individual"),
      });
    }

    const accountHolderTrust = legalArrangements.find((arr) => {
      const type = clean(arr?.arrangementType || arr?.type).toLowerCase();
      const source = clean(arr?.source).toLowerCase();
      return type === "trust" && source === "account_holder";
    });

    if (accountHolderTrust) {
      const parties = Array.isArray(accountHolderTrust?.parties)
        ? accountHolderTrust.parties
        : [];

      for (const p of parties) {
        const role = clean(p?.role).toLowerCase();
        const nm = pickName(p);
        if (!nm || !role) continue;
        if (/^class\b/i.test(nm) || /\bunnamed\b/i.test(nm)) continue;

        if (role === "trustee") {
          const trusteeKind = clean(p?.trusteeKind).toLowerCase();
          if (trusteeKind === "company" || trusteeKind === "corporate")
            continue;
          upsert(nm, "Trustee", {
            subjectType: "individual",
            countryContext: getCountryContextFromForm(form, nm, "individual"),
          });
          continue;
        }

        if (role === "settlor" || role === "beneficiary") {
          upsert(nm, role === "settlor" ? "Settlor" : "Beneficiary", {
            subjectType: "individual",
            countryContext: getCountryContextFromForm(form, nm, "individual"),
          });
        }
      }
    }

    for (const signatory of ownership?.signatories || []) {
      const person = linkedPerson(ownership, signatory);
      const subject = person && map.get(normKey(person.row.name));
      if (subject && !subject.roles.includes("Authorised signatory")) subject.roles.push("Authorised signatory");
    }
    return Array.from(map.values());
  }, [customerName, ownership, isEntityClient, form]);

  const blockedSubjects = useMemo(() => allScreeningSubjects.filter(needsFullName), [allScreeningSubjects]);
  const screeningSubjects = useMemo(() => allScreeningSubjects.filter(subject => !needsFullName(subject)), [allScreeningSubjects]);

  const individualNamesSignature = JSON.stringify(allScreeningSubjects.map(subject => [subject.key, nameFieldsForSubject(form, subject.name)]));
  const previousNamesSignature = useRef(savedScreening.individualNamesSignature || individualNamesSignature);
  useEffect(() => {
    if (previousNamesSignature.current === individualNamesSignature) return;
    previousNamesSignature.current = individualNamesSignature;
    if (readOnly) return;
    runTokenRef.current += 1;
    setIsLoading(false); setStage("idle"); setHasRun(false); setAssessmentCardReady(false);
    setCompletedRunAt(""); setLocalRows([]); setLiveAssessments({}); setAiSummaryLines([]);
    writeAt?.(["internalUse", "adverseMediaScreening"], null);
    writeAt?.(["adverseMedia"], { language });
  }, [individualNamesSignature, readOnly, writeAt, language]);

  const subjectCounts = useMemo(() => {
    const counts = {};
    for (const s of screeningSubjects || []) {
      for (const role of s?.roles || []) {
        counts[role] = (counts[role] || 0) + 1;
      }
    }
    return counts;
  }, [screeningSubjects]);

  const allRowsBySubjectKey = useMemo(() => {
    const kept = (Array.isArray(localRows) ? localRows : []).filter(
      keepRowByUrl
    );
    const m = new Map();

    for (const r of kept) {
      const k1 = subjectKeyFromRow(r);
      if (!m.has(k1)) m.set(k1, []);
      m.get(k1).push(r);

      const k2 = normKey(clean(r?.subjectName));
      if (k2 && k2 !== k1) {
        if (!m.has(k2)) m.set(k2, []);
        m.get(k2).push(r);
      }
    }

    return m;
  }, [localRows]);

  const displayRows = useMemo(() => (Array.isArray(localRows) ? localRows : []).filter(keepRowByUrl), [localRows]);

  const rowsBySubjectKey = useMemo(() => {
    const m = new Map();

    for (const r of displayRows) {
      const k1 = subjectKeyFromRow(r);
      if (!m.has(k1)) m.set(k1, []);
      m.get(k1).push(r);

      const k2 = normKey(clean(r?.subjectName));
      if (k2 && k2 !== k1) {
        if (!m.has(k2)) m.set(k2, []);
        m.get(k2).push(r);
      }
    }

    for (const [k, arr] of m.entries()) {
      arr.sort(
        (a, b) => (Number(b?.rankScore) || 0) - (Number(a?.rankScore) || 0)
      );
      m.set(k, arr);
    }

    return m;
  }, [displayRows]);

  const subjectIndicators = useMemo(() => {
    const out = new Map();
    for (const s of screeningSubjects) {
      const key = subjectKeyFromSubject(s);
      const allRows = allRowsBySubjectKey.get(key) || [];
      out.set(
        key,
        highestConcernArticleIndicators(allRows, liveAssessments)
      );
    }
    return out;
  }, [screeningSubjects, allRowsBySubjectKey, liveAssessments]);

  const subjectGroupedBullets = useMemo(() => {
    const out = new Map();
    for (const s of screeningSubjects) {
      const key = subjectKeyFromSubject(s);
      const allRows = allRowsBySubjectKey.get(key) || [];
      out.set(key, computeGroupedBulletsForSubject(allRows, liveAssessments));
    }
    return out;
  }, [screeningSubjects, allRowsBySubjectKey, liveAssessments]);

  // ✅ Modal dropdown options (subject switcher)
  const subjectOptions = useMemo(() => {
    return (screeningSubjects || []).map((s) => {
      const key = subjectKeyFromSubject(s);
      const hits = (allRowsBySubjectKey.get(key) || []).length;
      return {
        key,
        label: `${titleCaseName(s.name)}${hits ? ` (${hits})` : ""}`,
      };
    });
  }, [screeningSubjects, allRowsBySubjectKey]);

  function setScreeningRoot(patch) {
    writeAt?.(["internalUse", "adverseMediaScreening"], {
      ...(savedScreening || {}),
      ...(patch || {}),
    });
  }

  function setScreeningField(pathTail, value) {
    writeAt?.(["internalUse", "adverseMediaScreening", ...pathTail], value);
  }

  function setAdverseMedia(patch) {
    writeAt?.(["adverseMedia"], { ...(adverseMedia || {}), ...(patch || {}) });
  }

  function applySearchProgressFromServer(p) {
    const qTotal = Number(p?.queryTotal) > 0 ? Number(p.queryTotal) : 8;

    // subjectDone is 1-based in your backend
    const currentIndex1 = Math.max(1, Number(p?.subjectDone) || 1);

    setSearchProgressBySubject((prev) => {
      const next = { ...(prev || {}) };

      // 1) subjects BEFORE the current subject are guaranteed complete
      for (let i = 0; i < (screeningSubjects?.length || 0); i++) {
        const sk = subjectKeyFromSubject(screeningSubjects[i]);
        if (i < currentIndex1 - 1) {
          next[sk] = {
            ...(next[sk] || {}),
            done: qTotal,
            total: qTotal,
            pack: "done",
          };
        }
      }

      // 2) current subject from server cursor
      const currentSubjectName = clean(p?.subjectName || "");
      if (currentSubjectName) {
        const sk = subjectKeyFromSubject({ name: currentSubjectName });
        const qDone = Math.min(Number(p?.queryDone) || 0, qTotal);

        next[sk] = {
          ...(next[sk] || {}),
          done: qDone,
          total: qTotal,
          // pack: p?.pack || "",
          // queryKey: p?.queryKey || "",
        };
      }

      // 3) if server says done => mark all as done
      if (p?.stage === "done") {
        for (const s of screeningSubjects || []) {
          const sk = subjectKeyFromSubject(s);
          next[sk] = {
            ...(next[sk] || {}),
            done: qTotal,
            total: qTotal,
            pack: "done",
          };
        }
      }

      return next;
    });
  }

  async function runScreening() {
    if (readOnly || !screeningSubjects.length) return;
    const myToken = ++runTokenRef.current;
    runInProgressRef.current = true;
    clearTimeout(persistAssessmentsTimerRef.current);
    localRunStartedRef.current = true;

    try {
      setHasRun(true);
      setAssessmentCardReady(false);
      setCompletedRunAt("");
      setErrMsg("");
      setIsLoading(true);
      setStage("searching");

      setLiveAssessments({});
      setAssessProgress({ done: 0, total: 0 });
      setAssessProgressBySubject({});
      setLocalRows([]);

      setOverallProgress({
        searchDone: 0,
        searchTotal: 0,
        assessDone: 0,
        assessTotal: 0,
      });
      setSearchProgressBySubject({});
      candidateRowIdsRef.current = new Set();

      setAiSummaryLines([]);
      setAiSummaryError("");

      const screeningToken = await auth.currentUser?.getIdToken();

      const payload = {
        subjects: screeningSubjects.map((s) => ({
          name: s.name,
          ...nameFieldsForSubject(form, s.name),
          roles: s.roles,
          subjectType: s.subjectType || "individual",
          nationality: s?.countryContext?.nationality || "",
          domicileCountry: s?.countryContext?.domicileCountry || "",
          incorpCountry: s?.countryContext?.incorpCountry || "",
        })),
        language,
        screeningUsage: buildScreeningUsageContext(form, "adverse_media"),
        testMode: false,
      };

      // ✅ 1) Start job (returns jobId fast)
      const start = await apiFetch(
        "/myKyc/screening/adverse_media_serper_start",
        {
          token: screeningToken,
          body: payload,
        }
      );

      if (runTokenRef.current !== myToken) return;

      const jobId = start?.jobId;
      if (!jobId) throw new Error("Missing jobId from server");

      await trackScreeningUsage({
        form,
        screeningType: "adverse_media",
        namesCount: screeningSubjects.length,
        subjects: screeningSubjects,
        serperCredits: (screeningSubjects?.length || 0) * 8,
      });

      // ✅ 2) Poll status
      const pollEveryMs = 1200;
      const maxPollMs = 25 * 60 * 1000; // 25 minutes safety
      const t0 = Date.now();

      while (true) {
        if (runTokenRef.current !== myToken) return;

        const st = await apiFetch(
          `/myKyc/screening/adverse_media_serper_status/${jobId}`,
          { method: "GET" }
        );

        const p = st?.progress || {};

        applySearchProgressFromServer(p);

        // ✅ Update overall "searching progress" counters whenever present
        const searchDone = Number(p.searchDoneQueries) || 0;
        const searchTotal = Number(p.searchTotalQueries) || 0;

        if (searchTotal > 0) {
          lastSearchDoneRef.current = searchDone;
          lastSearchTotalRef.current = searchTotal;

          // only show search part during searching stage
          if (p.stage === "searching") {
            setOverallProgress((prev) => ({
              ...(prev || {}),
              searchDone,
              searchTotal,
            }));
          }
        }

        // ✅ Update per-subject searching bar EVEN if stage is "done"
        const subjectName = clean(p.subjectName || "");
        if (subjectName) {
          const sk = normKey(subjectName);
          const qDone = Number(p.queryDone) || 0;
          const qTotal = Number(p.queryTotal) || 0;

          if (qTotal > 0) {
            setSearchProgressBySubject((prev) => ({
              ...(prev || {}),
              [sk]: {
                done: Math.min(qDone, qTotal),
                total: qTotal,
                pack: p.pack || "",
                queryKey: p.queryKey || "",
              },
            }));
          }
        }

        if (st?.status === "failed") {
          throw new Error(st?.error || "Job failed");
        }

        // update loader stage if provided
        const stageFromServer = st?.progress?.stage;
        if (stageFromServer === "searching") setStage("searching");
        if (stageFromServer === "assessing") setStage("assessing");

        if (stageFromServer === "assessing") {
          // ✅ searching is finished, freeze all subject search bars at 100%
          const qTotal = Number(st?.progress?.queryTotal) || 8;

          setStage("assessing");
        }

        if (st?.status === "done") {
          // ✅ make sure every subject shows 100% search completion
          const qTotal = Number(st?.progress?.queryTotal) || 8;

          break;
        }

        if (Date.now() - t0 > maxPollMs) {
          throw new Error("Job timeout (took too long)");
        }

        await new Promise((r) => setTimeout(r, pollEveryMs));
      }

      // ✅ 3) Fetch result (this is the SAME payload shape as old server)
      const json = await apiFetch(
        `/myKyc/screening/adverse_media_serper_result/${jobId}`,
        { method: "GET" }
      );
      if (runTokenRef.current !== myToken) return;

      const rawRows = Array.isArray(json?.rows) ? json.rows : [];
      const rows = rawRows.filter(keepRowByUrl);
      setLocalRows(rows);

      // persist to firestore like before
      setScreeningRoot({
        individualNamesSignature,
        runAt: new Date().toISOString(),
        language,
        subjects: json?.subjects || [],
        rows,
        errors: json?.errors || [],
        searchConfiguration: json?.query || {},
        selectedIds: reconcileArticleSelections(localRows, Array.from(selectedIds), rows),
        assessmentsByRowId: {},
      });

      setAdverseMedia({ language });

      // ✅ Now you can assess locally like before OR reuse json.assessQueue
      let candidates = Array.isArray(json?.assessQueue) ? json.assessQueue : [];
      if (candidates.length) {
        candidates = candidates.map((c) => ({
          rowId: String(c?.rowId || ""),
          url: clean(c?.url),
          subjectName: clean(c?.subjectName),
          subjectKey: normKey(clean(c?.subjectKey || c?.subjectName)),

          // ✅ new fields coming from server assessQueue
          subjectType: clean(c?.subjectType || ""),
          queryLang: clean(c?.queryLang || ""),
          countryContext:
            typeof c?.countryContext === "object" && c.countryContext
              ? c.countryContext
              : {},
          snippet: clean(c?.snippet || ""),
          title: clean(c?.title || ""),
        }));
      }

      // If server didn’t return assessQueue, build from rows
      if (!candidates.length) {
        const seenUrl = new Set();
        candidates = [];
        for (const r of rows) {
          const rowId = String(r?.id || "");
          const url = clean(r?.link);
          const subjectName = clean(r?.subjectName);
          if (!rowId || !url) continue;
          const subjectArticleKey = JSON.stringify([subjectKeyFromRow(r), url]);
          if (seenUrl.has(subjectArticleKey)) continue;
          seenUrl.add(subjectArticleKey);

          candidates.push({
            rowId,
            url,
            subjectName,
            subjectKey: subjectKeyFromRow(r),

            // ✅ fallback from row (server now returns these on each row)
            subjectType: clean(r?.subjectType || ""),
            queryLang: clean(r?.queryLang || ""),
            countryContext:
              typeof r?.countryContext === "object" && r.countryContext
                ? r.countryContext
                : {},
          });
        }
      }

      candidateRowIdsRef.current = new Set(candidates.map(c => String(c.rowId)));

      // progress init (same as your old code)
      const totals = {};
      for (const c of candidates) {
        const sk = clean(c.subjectKey) || normKey(clean(c.subjectName));
        if (!sk) continue;
        totals[sk] = (totals[sk] || 0) + 1;
      }
      setAssessProgressBySubject(() => {
        const init = {};
        for (const sk of Object.keys(totals))
          init[sk] = { done: 0, total: totals[sk] };
        return init;
      });

      setStage("assessing");
      setAssessProgress({ done: 0, total: candidates.length });

      // ✅ define overall work units precisely now:
      // searchTotalQueries comes from last polled status if you store it;
      // easiest: compute here because it's deterministic (8 queries per subject)
      const { searchTotal, searchDone } = getSearchTotalsSafe();
      const assessTotal = candidates.length;

      // overall bar covers BOTH phases

      setOverallProgress({
        searchDone: searchTotal, // freeze search at 100% of its half
        searchTotal,
        assessDone: 0,
        assessTotal,
      });

      // Keep your existing concurrency worker (3)
      const CONCURRENCY = 3;
      let idx = 0;
      let done = 0;

      const assessmentsLocal = {}; // rowId -> {ok, assessment...} (complete by end)
      // The server resolves the organisation option from the authenticated user.
      const assessmentToken = await auth.currentUser?.getIdToken();

      async function worker() {
        while (idx < candidates.length) {
          const c = candidates[idx++];
          if (runTokenRef.current !== myToken) return;

          try {
            const out = await apiFetch(ASSESS_PATH, {
              token: assessmentToken,
              body: {
                url: c.url,
                name: c.subjectName,
                ...nameFieldsForSubject(form, c.subjectName),
                language,

                // ✅ new
                subjectType: c.subjectType || "individual",
                queryLang: c.queryLang || "en",
                countryContext: c.countryContext || {},
                snippet: c.snippet || "", // ✅ add this
                pageTitleHint: c.title || "", // optional
                screeningUsage: {
                  ...buildScreeningUsageContext(form, "adverse_media"),
                  subjectName: clean(c.subjectName),
                  rowId: clean(c.rowId),
                },
              },
              timeoutMs: 120000, // Scrape/base assessment plus the optional bounded 25s verification
              retries: 2,
              retryDelayMs: 1200,
            });

            if (runTokenRef.current !== myToken) return;

            assessmentsLocal[String(c.rowId)] = out; // ✅ HERE (success)

            setLiveAssessments((prev) => ({
              ...(prev || {}),
              [String(c.rowId)]: out,
            }));
          } catch (e) {
            if (runTokenRef.current !== myToken) return;

            const failObj = { ok: false, error: e?.message || String(e) };

            assessmentsLocal[String(c.rowId)] = failObj; // ✅ HERE (failure)

            setLiveAssessments((prev) => ({
              ...(prev || {}),
              [String(c.rowId)]: { ok: false, error: e?.message || String(e) },
            }));
          } finally {
            done++;
            setAssessProgress((p) => ({ ...(p || {}), done }));

            setOverallProgress((prev) => ({
              ...(prev || {}),
              assessDone: done,
            }));

            const sk = clean(c.subjectKey) || normKey(clean(c.subjectName));
            if (sk) {
              setAssessProgressBySubject((prev) => {
                const cur = prev?.[sk] || { done: 0, total: 0 };
                return {
                  ...(prev || {}),
                  [sk]: { ...cur, done: (cur.done || 0) + 1 },
                };
              });
            }
          }
        }
      }

      await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

      // ✅ 4) Single final summary call (after assessing completes)
      if (runTokenRef.current !== myToken) return;
      // Complete the result map before marking this phase finished. A missing
      // queue item is unassessed, never an indefinitely pending or clean result.
      for (const row of rows) {
        const rowId = String(row.id);
        if (!assessmentsLocal[rowId]) assessmentsLocal[rowId] = {
          ok: false, error: 'Article was not assessed in this screening.', code: 'not_assessed',
        };
      }
      clearTimeout(persistAssessmentsTimerRef.current);
      const completedAssessments = { ...assessmentsLocal };
      setLiveAssessments(completedAssessments);
      setScreeningField(["assessmentsByRowId"], completedAssessments);
      const completedAtIso = new Date().toISOString();
      setAssessmentCardReady(true);
      setCompletedRunAt(completedAtIso);
      setScreeningField(["completedAt"], completedAtIso);

      try {
        setAiSummaryLoading(true);
        setAiSummaryError("");

        // Use the local "rows" variable (NOT state) so we are deterministic
        const rowsKept = (Array.isArray(rows) ? rows : []).filter(keepRowByUrl);

        // Build rows by subject key (same logic you already use)
        const rowsBySubject = new Map();
        for (const r of rowsKept) {
          const sk = subjectKeyFromRow(r);
          if (!sk) continue;
          if (!rowsBySubject.has(sk)) rowsBySubject.set(sk, []);
          rowsBySubject.get(sk).push(r);
        }

        // Build per-subject output "as-is" + indicators
        const subjectSummaries = screeningSubjects.map((s) => {
          const key = subjectKeyFromSubject(s);
          const subjRows = rowsBySubject.get(key) || [];

          const ind = computeSummaryIndicators({
            allRowsForSubject: subjRows,
            liveAssessments: assessmentsLocal,
          });

          const enhancedConcern = enhancedSummaryConcern(subjRows, assessmentsLocal);
          const concernLabel = enhancedConcern?.concernLabel || concernLabelFromIndicators(ind);

          // const concernLabel =
          //   Number(ind?.overallConcern) >= 75
          //     ? "high concern"
          //     : Number(ind?.overallConcern) >= 50
          //     ? "elevated concern"
          //     : "no material concern identified";

          const grouped = computeGroupedBulletsForSubject(
            subjRows,
            assessmentsLocal
          );

          // create the exact "as-is" text block you want
          const targetedLines = grouped.targeted.map((b) => `- ${b.text}`);
          const hasContext = grouped.notTargeted.length > 0;

          const summaryText =
            (targetedLines.length
              ? `Concerns where ${titleCaseName(
                  s.name
                )} appears to be directly implicated :\n${targetedLines.join(
                  "\n"
                )}`
              : `No concerns have been identified where ${titleCaseName(
                  s.name
                )} appears to be directly implicated.`) +
            (hasContext
              ? `\nNote: some hits mention ${titleCaseName(
                  s.name
                )} in a contextual manner.`
              : "");

          return {
            subjectKey: key,
            subjectName: s.name,
            roles: Array.isArray(s.roles) ? s.roles : [],
            concernLabel,
            indicators: {
              overallConcern: enhancedConcern?.overallConcern ?? (Number(ind?.overallConcern) || 0),
              relatedness: Number(ind?.relatedness) || 0,
              sourceQuality: Number(ind?.sourceQuality) || 0,
              usableCount: Number(ind?.usableCount) || 0,
              excludedCount: Number(ind?.excludedCount) || 0,
              totalHits: Number(ind?.totalHits) || 0,
            },
            // these help the model reduce false positives
            counts: {
              targetedCount: grouped.targeted.length,
              contextualCount: grouped.notTargeted.length,
            },
            summaryText,
          };
        });

        const out = await apiFetch(SUMMARY_PATH, {
          body: {
            language,
            subjects: screeningSubjects.map((s) => ({
              name: s.name,
          ...nameFieldsForSubject(form, s.name),
              roles: s.roles,
            })),
            subjectSummaries,
            screeningUsage: {
              ...buildScreeningUsageContext(form, "adverse_media"),
              subjectsCount: screeningSubjects.length,
            },
          },
          timeoutMs: 90000,
          retries: 1,
          retryDelayMs: 1200,
        });

        if (runTokenRef.current !== myToken) return;

        const lines = Array.isArray(out?.lines) ? out.lines : [];
        setAiSummaryLines(lines);

        // ✅ persist in Firestore under internalUse.adverseMediaScreening.aiSummary
        setScreeningField(["aiSummary"], {
          lines,
          createdAt: new Date().toISOString(),
        });
      } catch (e) {
        setAiSummaryError(e?.message || String(e));
      } finally {
        setAiSummaryLoading(false);
      }
    } catch (e) {
      setErrMsg(e?.message || String(e));
    } finally {
      if (runTokenRef.current === myToken) {
        runInProgressRef.current = false;
        setIsLoading(false);
        setStage("idle");
      }
    }
  }

  useEffect(() => {
    onScreeningStateChange?.(isLoading);
  }, [onScreeningStateChange, isLoading]);

  useImperativeHandle(
    ref,
    () => ({
      runScreening,
      hasRun,
      isLoading,
      canRun: !readOnly && !isLoading && !!screeningSubjects?.length,
    }),
    [hasRun, isLoading, readOnly, screeningSubjects]
  );

  function toggleArticleSelection(id) {
    const sid = String(id);
    const next = new Set(selectedIds);
    if (next.has(sid)) next.delete(sid);
    else next.add(sid);
    setScreeningField(["selectedIds"], Array.from(next));
  }

  const modalRows = useMemo(() => {
    const key = clean(activeSubjectKey);
    if (!key) return [];
    const assessed = rowsBySubjectKey.get(key) || [];
    if (assessed.length) return assessed.slice();
    const raw = allRowsBySubjectKey.get(key) || [];
    return raw.slice();
  }, [activeSubjectKey, rowsBySubjectKey, allRowsBySubjectKey]);

  const activeSubject = useMemo(() => {
    const k = clean(activeSubjectKey);
    if (!k) return null;
    return (
      screeningSubjects.find((x) => subjectKeyFromSubject(x) === k) || null
    );
  }, [activeSubjectKey, screeningSubjects]);

  const negativeNewsOptions = useMemo(
    () => [
      { label: "No negative news", value: false },
      { label: "Negative news", value: true },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { label: "Remediated", value: "remediated" },
      { label: "Confirmed", value: "confirmed" },
    ],
    []
  );

  const negativeNews = adverseMedia?.negativeNews;
  const status = adverseMedia?.status;
  const statusDisabled = readOnly || !!blockedSubjects.length || negativeNews !== true;

  const lastRunAt = completedRunAt ? new Date(completedRunAt) : null;
  const screeningRunCompleted = assessmentCardReady && !!completedRunAt;
  const complianceCardLoading = isLoading || aiSummaryLoading;
  const complianceContentReady = assessmentCardReady;

  return (
    <div className="card kycCard mb-3 kycAdverseMediaSection">
      {!embedded ? (
        <div className="kycSectionHead">
          <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <h5 className="kycSectionTitle mb-0">
              {sectionNo} | Adverse Media Screening
            </h5>

            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: 180, display: "none" }}
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={readOnly || isLoading}
              >
                <option value="en">English</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="it">Italian</option>
                <option value="de">German</option>
              </select>

            </div>
          </div>
        </div>
      ) : null}

      <div className="kycSectionBody">
        <ScreeningNameNotice subjects={blockedSubjects} readOnly={readOnly} />
        {/* <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2"> */}
        {/* <div className="text-muted small">
            Subjects:{" "}
            <span className="fw-semibold">{screeningSubjects.length || 0}</span>
            {lastRunAt ? (
              <>
                {" "}
                · Last run:{" "}
                <span className="fw-semibold">
                  {lastRunAt.toLocaleString()}
                </span>
              </>
            ) : null}
          </div> */}

        {/* <div className="d-flex align-items-center gap-2">
            {badge(
              articleSelectionLabel(selectedIds.size),
              "grey"
            )}
            {badge(`${displayRows.length} assessed hits`, "grey")}
          </div> */}
        {/* </div> */}

        <AnimatePresence initial={false}>
          {!!errMsg && (
            <motion.div
              className="alert alert-danger py-2 mb-3"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
            >
              {errMsg}
            </motion.div>
          )}
        </AnimatePresence>

        {/* --------------------------------------------------------------- */}

        {/* -------------------- TOP OVERVIEW CARD (Adverse Media) -------------------- */}
        {!embedded || readOnly
          ? (() => {
              const hitsCount = (
                Array.isArray(localRows) ? localRows : []
              ).filter(keepRowByUrl).length;
              const confirmedCount = selectedIds.size;

              const screeningPending = !hasRun;

              // Assessment term rules
              const assessmentPending =
                negativeNews == null || (negativeNews === true && !status);

              const assessmentTerm = assessmentPending
                ? "Pending"
                : negativeNews === false
                ? "No negative news"
                : status === "remediated"
                ? "Remediated"
                : "Confirmed";

              const assessmentScheme = assessmentPending
                ? "grey"
                : negativeNews === true && status === "confirmed"
                ? "danger"
                : negativeNews === false || status === "remediated"
                ? "success"
                : "grey";

              const assessmentDescription = assessmentPending
                ? "Awaiting assessment"
                : negativeNews === false
                ? "No negative news identified"
                : status === "remediated"
                ? "Negative news identified and remediated"
                : "Confirmed negative news identified";

              // Final outcome rules
              const finalState =
                !hasRun || assessmentPending
                  ? "pending"
                  : negativeNews === true && status === "confirmed"
                  ? "confirmed"
                  : "cleared";

              const overall =
                finalState === "confirmed"
                  ? {
                      term: "Confirmed",
                      bg: "#FDEBEC",
                      border: "#F3B6BC",
                      color: "#C88294",
                      lottie: overallAlertAnim,
                    }
                  : finalState === "cleared"
                  ? {
                      term: "Cleared",
                      bg: "#EAF7EE",
                      border: "#BFE6CC",
                      color: "#6B7C5F",
                      lottie: overallClearedAnim,
                    }
                  : {
                      term: "Pending",
                      bg: "#F4F5F7",
                      border: "#D9DCE1",
                      color: "#6B7C5F",
                      lottie: overallPendingAnim,
                    };

              const canToggleDetails =
                !readOnly ||
                hasRun ||
                isLoading ||
                !!complianceAssessmentContent;

              return (
                <div
                  className="p-3 border rounded-3 mb-3"
                  style={{
                    background: "#EBECEF",
                    cursor: canToggleDetails ? "pointer" : "default",
                  }}
                  role={canToggleDetails ? "button" : undefined}
                  tabIndex={canToggleDetails ? 0 : undefined}
                  onClick={() => {
                    if (canToggleDetails) setShowDetails((v) => !v);
                  }}
                  onKeyDown={(e) => {
                    if (!canToggleDetails) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowDetails((v) => !v);
                    }
                  }}
                >
                  {/* Title row (icon + title) */}
                  <div
                    className="d-flex align-items-start justify-content-between gap-2 flex-wrap"
                    style={{ height: 30, marginBottom: 5, marginTop: 0 }}
                  >
                    <div>
                      <div
                        className="fw-semibold d-flex align-items-center gap-2"
                        style={{ color: "#2f3441" }}
                      >
                        <span
                          style={{
                            width: 40,
                            height: 40,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                          }}
                        >
                          <Lottie
                            animationData={adverseMediaAnim}
                            loop
                            autoplay
                            style={{ width: 40, height: 40 }}
                          />
                        </span>
                        Adverse media overview
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-center gap-2"
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {(hasRun || isLoading) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center justify-content-center"
                          style={{ height: 30, width: 30 }}
                          aria-label={isLoading ? "Screening in progress" : "Re-run screening"}
                          disabled={readOnly || isLoading || !screeningSubjects.length}
                          onClick={(e) => {
                            e.stopPropagation();
                            runScreening();
                          }}
                        >
                          <i className="bi bi-arrow-repeat" aria-hidden="true" />
                        </button>
                      )}
                      {canToggleDetails ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDetails((v) => !v);
                          }}
                          style={{ height: 30 }}
                        >
                          {showDetails ? (
                            <>
                              <i className="bi bi-chevron-up me-1" />
                              Hide details
                            </>
                          ) : (
                            <>
                              <i className="bi bi-chevron-down me-1" />
                              Show details
                            </>
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <hr className="my-3" />

                  <div className="row g-2">
                    {/* Screening */}
                    <div className="col-12 col-md">
                      <div className="p-2 rounded-3 border bg-white h-100">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="d-inline-flex align-items-center justify-content-center"
                              style={{
                                width: 22,
                                height: 22,
                                color: "#817672",
                                flex: "0 0 auto",
                              }}
                              aria-hidden="true"
                            >
                              <svg
                                height="512"
                                viewBox="0 0 64 64"
                                width="512"
                                xmlns="http://www.w3.org/2000/svg"
                                id="fi_6772342"
                              >
                                <g
                                  id="_10-Security_Gate"
                                  data-name="10-Security Gate"
                                >
                                  <path
                                    fill="currentColor"
                                    d="m53 2h-42a.99974.99974 0 0 0 -1 1v58a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-43h32v43a.99974.99974 0 0 0 1 1h4a.99974.99974 0 0 0 1-1v-58a.99974.99974 0 0 0 -1-1zm-39 58h-2v-56h2zm34-44h-32v-12h32zm4 44h-2v-56h2zm-26-46h12a.99974.99974 0 0 0 1-1v-6a.99974.99974 0 0 0 -1-1h-12a.99974.99974 0 0 0 -1 1v6a.99974.99974 0 0 0 1 1zm1-6h10v4h-10zm-9 0a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm23-4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm0 4a.99974.99974 0 0 1 1-1h3a1 1 0 0 1 0 2h-3a.99974.99974 0 0 1 -1-1zm-4 20h-.54047a6 6 0 1 0 -8.91906 0h-.54047a7.00818 7.00818 0 0 0 -7 7v6a3.00328 3.00328 0 0 0 3 3h2v11a3.00328 3.00328 0 0 0 3 3h8a3.00328 3.00328 0 0 0 3-3v-11h2a3.00328 3.00328 0 0 0 3-3v-6a7.00818 7.00818 0 0 0 -7-7zm-5-8a4 4 0 1 1 -4 4 4.00427 4.00427 0 0 1 4-4zm10 21a1.001 1.001 0 0 1 -1 1h-2v-5a1 1 0 0 0 -2 0v18a1.001 1.001 0 0 1 -1 1h-3v-9a1 1 0 0 0 -2 0v9h-3a1.001 1.001 0 0 1 -1-1v-18a1 1 0 0 0 -2 0v5h-2a1.001 1.001 0 0 1 -1-1v-6a5.00588 5.00588 0 0 1 5-5h10a5.00588 5.00588 0 0 1 5 5z"
                                  />
                                </g>
                              </svg>
                            </span>

                            <div className="fw-semibold small">Screening</div>
                          </div>

                          {screeningPending ? (
                            badge("Pending", "grey")
                          ) : (
                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                              {badge(`${hitsCount} hits`, "grey")}
                              {badge(
                                articleSelectionLabel(confirmedCount),
                                "grey"
                              )}
                            </div>
                          )}
                        </div>

                        <div className="small text-muted mt-1">
                          {screeningPending ? (
                            <>Run screening to generate results</>
                          ) : (
                            <>
                              Subjects:{" "}
                              <span className="fw-semibold">
                                {screeningSubjects.length}
                              </span>
                              {lastRunAt ? (
                                <>
                                  {" "}
                                  · Last run:{" "}
                                  <span className="fw-semibold">
                                    {lastRunAt.toLocaleString()}
                                  </span>
                                </>
                              ) : null}
                            </>
                          )}
                        </div>

                        {/* optional: loader (keeps your existing UX) */}
                        {isLoading ? (
                          <div className="mt-2">
                            <div className="d-flex align-items-center gap-2 text-muted small">
                              <span>
                                {aiSummaryLoading ? "Preparing findings summary…" : `Screening in progress (${
                                  stage === "assessing"
                                    ? "Assessing hits..."
                                    : "Searching sources..."
                                })`}
                              </span>
                              {/* <span>
                                {stage === "assessing"
                                  ? "Assessing hits…"
                                  : "Searching sources…"}
                              </span> */}
                            </div>
                          </div>
                        ) : null}
                        {/* {isLoading ? (
                          <SubjectLoader
                            stage={stage}
                            done={
                              stage === "searching"
                                ? Number(lastSearchDoneRef.current) || 0
                                : assessProgress.done
                            }
                            total={
                              stage === "searching"
                                ? Number(lastSearchTotalRef.current) || 0
                                : assessProgress.total
                            }
                            searchingLabel="Searching sources…"
                            assessingLabel="Assessing…"
                            waitingLabel="Waiting for AI assessment…"
                          />
                        ) : null} */}
                        {/* {isLoading ? (
                          <SubjectLoader
                            stage={stage}
                            done={assessProgress.done}
                            total={assessProgress.total}
                          />
                        ) : null} */}
                      </div>
                    </div>

                    {/* Assessment */}
                    <div className="col-12 col-md">
                      <div className="p-2 rounded-3 border bg-white h-100">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className="d-inline-flex align-items-center justify-content-center"
                              style={{
                                width: 22,
                                height: 22,
                                color: "#817672",
                                flex: "0 0 auto",
                              }}
                              aria-hidden="true"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                id="fi_14979987"
                                viewBox="0 0 60 60"
                              >
                                <path
                                  fill="currentColor"
                                  d="M36.949,31.846A3.005,3.005,0,0,0,39,29V26a9,9,0,0,0-18,0v3a3.005,3.005,0,0,0,2.051,2.846A7,7,0,0,0,26,36.734v1.5l-3.9,1.053a7.242,7.242,0,0,0-5.228,4.542L15.6,47.4l-2.4-1.917a1.579,1.579,0,0,0-.2-.13V42.68a2.685,2.685,0,0,0-5.29-.642l-.007.026a4.629,4.629,0,0,0-4.211,1.453,1.892,1.892,0,0,0,.617,2.995,19.054,19.054,0,0,1,3.045,1.7c.465.336.843.633,1.214.935a1.83,1.83,0,0,0,.651,1.2l6.43,4.77a3.45,3.45,0,0,0,3.086.8,3.406,3.406,0,0,0,2.415-1.958L22,51.992V58a2,2,0,0,0,2,2H36a2,2,0,0,0,2-2V51.987l1.013,1.892a3.456,3.456,0,0,0,2.452,2.036,3.4,3.4,0,0,0,3.016-.744l6.568-4.882a1.769,1.769,0,0,0,.583-1.14c.371-.3.747-.6,1.207-.933a19.142,19.142,0,0,1,3.055-1.706,1.891,1.891,0,0,0,.6-3,4.641,4.641,0,0,0-4.2-1.442l-.007-.029A2.685,2.685,0,0,0,47,42.68v2.668a1.718,1.718,0,0,0-.2.131l-2.326,1.937-1.345-3.588a7.219,7.219,0,0,0-5.184-4.53L34,38.234v-1.5A7,7,0,0,0,36.949,31.846ZM30,19a7.009,7.009,0,0,1,7,7v.179c-.037-.013-.072-.032-.11-.044a3.073,3.073,0,0,0-4.626-1.743,4.521,4.521,0,0,1-4.527,0,3.075,3.075,0,0,0-4.629,1.743c-.037.011-.071.03-.108.043V26A7.009,7.009,0,0,1,30,19ZM24,30a1,1,0,0,1,0-2,1.081,1.081,0,0,0,1-1.075,1.031,1.031,0,0,1,.564-.9,1.1,1.1,0,0,1,.51-.129,1.023,1.023,0,0,1,.564.169,6.5,6.5,0,0,0,6.723,0,1.041,1.041,0,0,1,1.073-.04A1.064,1.064,0,0,1,35,27a1,1,0,0,0,1,1,1,1,0,0,1,0,2,1,1,0,0,0-1,1,5,5,0,0,1-10,0A1,1,0,0,0,24,30Zm9.439,10.155,1.921.517a6.768,6.768,0,0,1-2.036,2.614.392.392,0,0,1-.365.049.38.38,0,0,1-.251-.261,5.694,5.694,0,0,0-.793-1.654A4.831,4.831,0,0,0,33.439,40.155ZM31.984,52.646,30,53.965l-1.984-1.319L28.862,47h2.276ZM28.721,45l-.116-.348a2.294,2.294,0,0,0,.6-1.006A3.239,3.239,0,0,1,30,42.215a3.2,3.2,0,0,1,.788,1.417,2.323,2.323,0,0,0,.608,1.019L31.279,45Zm-1.433-1.911a.373.373,0,0,1-.247.246.4.4,0,0,1-.37-.052,6.8,6.8,0,0,1-2.034-2.609l1.924-.519a4.851,4.851,0,0,0,1.525,1.266A5.738,5.738,0,0,0,27.288,43.089ZM5.075,44.754a2.634,2.634,0,0,1,2.935-.522,1,1,0,0,0,1.38-.669l.259-1.037A.7.7,0,0,1,10.32,42a.681.681,0,0,1,.68.68v2.769a1.8,1.8,0,0,0-.233.205l-1.524,1.63c-.28-.221-.581-.451-.924-.7A20.843,20.843,0,0,0,5.075,44.754ZM49.68,42a.7.7,0,0,1,.671.522l.259,1.041a1,1,0,0,0,1.38.669,2.69,2.69,0,0,1,2.962.509,20.887,20.887,0,0,0-3.278,1.849c-.339.245-.637.473-.916.693l-1.525-1.629A1.8,1.8,0,0,0,49,45.449V42.68A.681.681,0,0,1,49.68,42Zm-8.424,2.53L42.6,48.118a1.956,1.956,0,0,0,3.052.84l2.248-1.8,1.647,1.759-6.327,4.7a1.478,1.478,0,0,1-1.321.342,1.542,1.542,0,0,1-1.087-.949l-2.931-5.486A1,1,0,0,0,36,48V58H24V48a1,1,0,0,0-1.882-.472l-2.969,5.564a1.481,1.481,0,0,1-1.049.871,1.532,1.532,0,0,1-1.389-.4L10.45,48.921,12.1,47.159l2.248,1.8a1.955,1.955,0,0,0,3.052-.84l1.345-3.586a5.271,5.271,0,0,1,3.832-3.3l.1-.027a9.027,9.027,0,0,0,2.805,3.692,2.356,2.356,0,0,0,1.241.443l.25.75-.956,6.388a1.828,1.828,0,0,0,.794,1.774L28.989,55.7a1.823,1.823,0,0,0,2.022,0l2.176-1.446a1.831,1.831,0,0,0,.793-1.781l-.955-6.381.247-.742a2.359,2.359,0,0,0,1.239-.449,8.969,8.969,0,0,0,2.806-3.7l.151.04A5.247,5.247,0,0,1,41.256,44.53ZM32,38.743a2.944,2.944,0,0,1-2,1.243,2.946,2.946,0,0,1-2-1.241V37.706a6.95,6.95,0,0,0,4,0Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M11,22A11,11,0,1,0,0,11,11.013,11.013,0,0,0,11,22ZM11,2a9,9,0,1,1-9,9A9.01,9.01,0,0,1,11,2Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M49,0A11,11,0,1,0,60,11,11.013,11.013,0,0,0,49,0Zm0,20a9,9,0,1,1,9-9A9.01,9.01,0,0,1,49,20Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M7.293,14.707a1,1,0,0,0,1.414,0L11,12.414l2.293,2.293a1,1,0,0,0,1.414-1.414L12.414,11l2.293-2.293a1,1,0,1,0-1.414-1.414L11,9.586,8.707,7.293A1,1,0,1,0,7.293,8.707L9.586,11,7.293,13.293A1,1,0,0,0,7.293,14.707Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M51.168,7.445l-3.323,4.986-1.138-1.138a1,1,0,0,0-1.414,1.414l2,2A1,1,0,0,0,48,15c.032,0,.065,0,.1-.005a1,1,0,0,0,.733-.44l4-6a1,1,0,0,0-1.664-1.11Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M50,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                                ></path>
                                <path
                                  fill="currentColor"
                                  d="M12,37V25a1,1,0,0,0-2,0V37a1,1,0,0,0,2,0Z"
                                ></path>
                              </svg>
                            </span>
                            <div className="fw-semibold small">Assessment</div>
                          </div>

                          {badge(assessmentTerm, assessmentScheme)}
                        </div>

                        <div className="small text-muted mt-1">
                          {assessmentDescription}
                        </div>
                      </div>
                    </div>

                    {/* Final outcome (animated icon + label only) */}
                    <div className="col-12 col-md-3">
                      <div
                        className="p-2 rounded-3 border h-100 d-flex align-items-center justify-content-center text-center"
                        style={{
                          background: overall.bg,
                          borderColor: overall.border,
                        }}
                      >
                        <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                          <span
                            style={{
                              width: 35,
                              height: 35,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flex: "0 0 auto",
                              lineHeight: 1,
                            }}
                            aria-hidden="true"
                          >
                            <Lottie
                              animationData={overall.lottie}
                              loop
                              autoplay
                              style={{ width: 35, height: 35 }}
                            />
                          </span>

                          <div
                            className="fw-semibold"
                            style={{ color: overall.color, fontSize: 12 }}
                          >
                            {overall.term}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          : null}

        <AnimatePresence initial={false}>
          {detailsVisible ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: "hidden" }}
            >
              {/* -------------------- COMPLIANCE ASSESSMENT CARD -------------------- */}
              {!hideComplianceAssessmentCard ? (
                <div className="kycAdverseComplianceCard rounded-3 p-3 mt-3" aria-busy={complianceCardLoading}>
                  <div className={`kycAdverseComplianceBody ${complianceContentReady ? "is-ready" : complianceCardLoading ? "is-loading" : "is-idle"}`}>
                    {!complianceContentReady && (
                      <div className="kycAdverseCompliancePending text-center">
                        {complianceCardLoading ? (
                            <AdverseMediaLoadingStatus label={aiSummaryLoading
                              ? "Preparing findings summary…"
                              : stage === "assessing"
                              ? `Reviewing articles${assessProgress.total ? ` · ${assessProgress.done} of ${assessProgress.total}` : "…"}`
                              : "Searching media sources…"} />
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="text-muted small mb-2">
                      Run adverse media screening on{" "}
                      {isEntityClient
                        ? "the client and related parties."
                        : "the customer name."}
                    </div>

                    <div className="small text-muted mb-2">
                      {screeningSubjects?.length ? (
                        <>
                          Screening {screeningSubjects.length} name
                          {screeningSubjects.length > 1 ? "s" : ""} ·{" "}
                          <span className="fw-semibold">
                            {subjectCounts.Client || 0}
                          </span>{" "}
                          client
                        </>
                      ) : (
                        "No name to screen yet."
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary kycPepSearchBtn"
                      style={{
                        width: 150,
                        minHeight: 38,
                        padding: "10px 18px",
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                      }}
                      onClick={runScreening}
                      disabled={
                        readOnly || isLoading || screeningSubjects.length === 0
                      }
                    >
                      <img
                        src={doubleTapFullIcon}
                        alt=""
                        aria-hidden="true"
                        style={{ width: 32, height: 32, flex: "0 0 auto" }}
                      />
                      {isLoading ? "Screening…" : "Run screening"}
                    </button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className={`kycAdverseComplianceContent ${complianceContentReady ? "is-ready" : ""}`}
                      aria-hidden={!complianceContentReady} inert={!complianceContentReady}>
                      <div className="kycAdverseComplianceTitle mb-2">Compliance assessment</div>
              <div className="row g-3 align-items-start">
                <div className="col-4">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Negative news</div>

                    <div
                      className={`kycSelectDecisions ${
                        negativeNews === true
                          ? "is-no"
                          : negativeNews === false
                          ? "is-yes"
                          : ""
                      }`}
                    >
                      <SelectButton
                        value={negativeNews ?? null}
                        options={negativeNewsOptions}
                        onChange={(e) => {
                          const v = e.value;
                          if (v === true)
                            setAdverseMedia({ negativeNews: true });
                          if (v === false) {
                            setAdverseMedia({
                              negativeNews: false,
                              status: null,
                            });
                            scrollAdverseAnchorThenCollapse();
                          }
                          if (v == null)
                            setAdverseMedia({
                              negativeNews: null,
                              status: null,
                            });
                        }}
                        disabled={readOnly || !!blockedSubjects.length || isLoading || !assessmentCardReady}
                      />
                    </div>
                  </div>

                  <div className={statusDisabled ? "is-disabled-strong" : ""}>
                    <div className="text-muted small mb-1">Status</div>

                    <div
                      className={`kycSelectDecisions ${
                        status === "confirmed"
                          ? "is-no"
                          : status === "remediated"
                          ? "is-yes"
                          : ""
                      }`}
                    >
                      <SelectButton
                        value={status ?? null}
                        options={statusOptions}
                        onChange={(e) => {
                          const nextStatus = e.value;
                          setAdverseMedia({ status: nextStatus });
                          if (
                            nextStatus === "remediated" ||
                            nextStatus === "confirmed"
                          ) {
                            scrollAdverseAnchorThenCollapse();
                          }
                        }}
                        disabled={statusDisabled || isLoading || !assessmentCardReady}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-8">
                  <div className="text-muted small mb-1">
                    Compliance comments (optional)
                  </div>
                  <textarea
                    className={`form-control ${cc(["adverseMedia", "comment"])}`}
                    rows={5}
                    value={adverseMedia?.comment || ""}
                    onChange={(e) =>
                      setAdverseMedia({ comment: e.target.value })
                    }
                    disabled={readOnly || !!blockedSubjects.length || isLoading || !assessmentCardReady}
                    placeholder=""
                  />
                </div>

                <div className="col-12 kycAdverseComplianceSummary">
                  <div className="kycAdverseSummaryCard">
                  <div className="kycAdverseSummaryTitle">
                    Summary of findings
                  </div>

                  {aiSummaryLoading ? (
                    <div className="kycAdverseSummaryPending text-muted small" role="status">
                      Preparing findings summary…
                    </div>
                  ) : aiSummaryError ? (
                    <div className="text-muted small" role="status">
                      The summary is temporarily unavailable. Please try again later.
                    </div>
                  ) : aiSummaryLines.length ? (
                    <div className="kycAdverseSummaryResult">
                      <p className="kycAdverseSummaryOverall">
                        <OverallSummaryLine line={aiSummaryLines[0]} />
                      </p>
                      {aiSummaryLines.length > 1 && <ul className="kycAdverseSummaryFindings">
                        {aiSummaryLines.slice(1, 10).map((line, i) => (
                          <li key={i} className="kycAdverseSummaryFinding">
                            <SummaryFinding line={line} subjects={screeningSubjects} />
                          </li>
                        ))}
                      </ul>}
                    </div>
                  ) : (
                    <div className="text-muted small">No summary yet.</div>
                  )}
                  </div>
                </div>
              </div>
                    </div>
                  </div>
                </div>
              ) : null}
              {/* --- END compliance card --- */}

              {hideComplianceAssessmentCard && complianceAssessmentContent ? (
                <div className="mt-3">{complianceAssessmentContent}</div>
              ) : null}

              {/* OVERALL PROGRESS BAR  */}

              {isLoading ? (
                <>
                <OverallProgressBar
                  stage={stage}
                  searchDone={overallProgress.searchDone}
                  searchTotal={overallProgress.searchTotal}
                  assessDone={overallProgress.assessDone}
                  assessTotal={overallProgress.assessTotal}
                  label={`Screening progress (${
                    stage === "assessing"
                      ? "Assessing hits..."
                : "Searching sources..."
                  })`}
                />
                </>
              ) : null}

        {/* {isLoading ? (
          <OverallProgressBar
            stage={stage}
            searchDone={overallProgress.searchDone}
            searchTotal={overallProgress.searchTotal}
            assessDone={overallProgress.assessDone}
            assessTotal={overallProgress.assessTotal}
            label="Overall run progress"
          />
        ) : null} */}

        {/* ---- SUBJECT CARD ----- */}

        {!screeningSubjects.length ? (
          blockedSubjects.length ? null : <div className="text-muted">No subjects found.</div>
        ) : (
          <div className="d-flex flex-column gap-3 mb-3">
            {screeningSubjects.map((s) => {
              const key = subjectKeyFromSubject(s);

              const allRowsForSubject = allRowsBySubjectKey.get(key) || [];
              const assessedRowsForModal = rowsBySubjectKey.get(key) || [];
              const hasUnavailableAssessments = assessedRowsForModal.some((r) => {
                const result = liveAssessments?.[String(r?.id)];
                return (result && result.ok !== true) ||
                  result?.assessment?.articleUsable === false ||
                  (assessmentCardReady && !result);
              });

              const confirmedCount = assessedRowsForModal.filter((r) =>
                selectedIds.has(String(r?.id))
              ).length;

              const canOpen = (allRowsForSubject.length || 0) > 0;

              const ind = subjectIndicators.get(key) || highestConcernArticleIndicators([], {});

              const grouped = subjectGroupedBullets.get(key) || {
                targeted: [],
                notTargeted: [],
              };

              const pSearch = searchProgressBySubject?.[key] || {
                done: 0,
                total: 0,
                pack: "",
              };
              const pAssess = assessProgressBySubject?.[key] || {
                done: 0,
                total: 0,
              };

              const subjectIsLoadingSearching =
                isLoading && stage === "searching";

              const subjectIsLoadingAssessing =
                isLoading &&
                stage === "assessing" &&
                // still assessing this subject
                ((pAssess.total > 0 && pAssess.done < pAssess.total) ||
                  // OR: search is done for this subject, but AI assessment hasn't started/queued for it
                  (pAssess.total === 0 &&
                    !assessmentCardReady &&
                    pSearch.total > 0 &&
                    pSearch.done >= pSearch.total));

              const subjectIsLoading =
                subjectIsLoadingSearching || subjectIsLoadingAssessing;

              const detailsCount =
                (assessedRowsForModal.length || 0) > 0
                  ? assessedRowsForModal.length
                  : allRowsForSubject.length;

              return (
                <div
                  key={key}
                  className="card"
                  aria-busy={subjectIsLoading}
                  style={{ borderRadius: 12, backgroundColor: "#FAFAFA" }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div style={{ minWidth: 260, flex: "1 1 520px" }}>
                        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div>
                            <div className="fw-semibold">
                              {titleCaseName(s.name)}
                            </div>
                            <div className="text-muted small">
                              {(Array.isArray(s.roles) ? s.roles : [])
                                .slice(0, 3)
                                .join(", ")}
                              {(Array.isArray(s.roles) ? s.roles : []).length >
                              3
                                ? "…"
                                : ""}
                            </div>
                          </div>

                          {screeningRunCompleted ? (
                            <div className="d-flex align-items-center gap-2">
                              {subjectConcernBadge(ind)}
                              {badge(
                                confirmedHitsLabel(confirmedCount),
                                "grey"
                              )}
                            </div>
                          ) : null}

                          {/* <div className="d-flex align-items-center gap-2">
                            {subjectConcernBadge(ind)}
                            {badge(
                              articleSelectionLabel(confirmedCount),
                              "grey"
                            )}
                          </div> */}

                          {/* <div className="d-flex align-items-center gap-2">
                            {badge(`${allRowsForSubject.length} hits`, "grey")}
                            {badge(
                              articleSelectionLabel(confirmedCount),
                              "grey"
                            )}
                          </div> */}
                        </div>

                        {!hasRun ? (
                          <div className="text-muted small mt-2">
                            Click <b>Run screening</b> to generate results.
                          </div>
                        ) : subjectIsLoading ? (
                          <>
                          <SubjectLoader
                            stage={stage}
                            done={
                              stage === "searching"
                                ? pSearch.done
                                : pAssess.done
                            }
                            total={
                              stage === "searching"
                                ? pSearch.total
                                : pAssess.total
                            }
                            searchingLabel="Searching sources…"
                            assessingLabel="Assessing hits…"
                            waitingLabel="Waiting for AI assessment…"
                          />
                          <AdverseMediaPlaceholder />
                          </>
                        ) : (
                          // <SubjectLoader
                          //   stage={stage}
                          //   done={pSubj.done}
                          //   total={pSubj.total}
                          //   label="Searching sources…"
                          // />
                          <>
                            <div className="mt-3 d-flex align-items-stretch gap-3 flex-wrap">
                              <div className="kycAdverseSubjectFindings" style={{ minWidth: 260, flex: "1 1 520px" }}>
                                {grouped.targeted.length ? (
                                  <div className="mb-2">
                                    <div className="fw-semibold small mb-1">
                                      Concerns where {titleCaseName(s.name)}{" "}
                                      appears to be directly implicated :
                                    </div>
                                    <ul className="text-muted small mb-0 ps-3">
                                      {grouped.targeted.map((b, i) => (
                                        <li
                                          key={`t-${i}`}
                                          className="kycBulletNotConfirmed"
                                        >
                                          {b.text}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}

                                {grouped.notTargeted.length &&
                                !grouped.targeted.length ? (
                                  <div className="fw-semibold small mb-1">
                                    No concerns have been identified where{" "}
                                    {titleCaseName(s.name)} appears to be
                                    directly implicated.
                                  </div>
                                ) : null}

                                {!grouped.targeted.length &&
                                !grouped.notTargeted.length &&
                                !hasUnavailableAssessments && !ind.uncertainCount ? (
                                  <div className="text-muted small">
                                    No AI summary yet for this subject.
                                  </div>
                                ) : null}

                                {grouped.notTargeted.length > 0 || hasUnavailableAssessments || ind.uncertainCount > 0 ? (
                                  <div className="kycAdverseSubjectNote small">
                                    <strong>Note:</strong>{" "}{grouped.notTargeted.length > 0 ? (
                                      <>Some hits mention {titleCaseName(s.name)} in a contextual manner.{" "}</>
                                    ) : null}
                                    {hasUnavailableAssessments ? (
                                      <>{grouped.notTargeted.length > 0 ? "Also, some" : "Some"} articles could not be assessed and need to be reviewed manually.{" "}</>
                                    ) : null}
                                    {ind.uncertainCount > 0 ? (
                                      <>Some hits have an uncertain name match or assessment and need to be reviewed manually.</>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>

                              <div style={{ minWidth: 320, flex: "0 1 420px", maxWidth: "100%" }}>
                                <AdverseMediaSubjectIndicators indicators={ind} />
                              </div>
                            </div>

                            <div className="mt-3 d-grid d-md-inline-block">
                              <button
                                type="button"
                                className="btn btn-sm btn-viewDetails"
                                disabled={!canOpen}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  // ✅ if only one subject exists, force it
                                  const onlyKey =
                                    (screeningSubjects || []).length === 1
                                      ? subjectKeyFromSubject(
                                          screeningSubjects[0]
                                        )
                                      : key;

                                  setActiveSubjectKey(onlyKey);
                                  setModalOpen(true);
                                }}
                                // onClick={(e) => {
                                //   e.preventDefault();
                                //   e.stopPropagation();
                                //   setActiveSubjectKey(key);
                                //   setModalOpen(true);
                                // }}
                                title={
                                  canOpen
                                    ? "Open detailed results"
                                    : "No rows yet"
                                }
                              >
                                View details ({detailsCount})
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* -------------------- Modal -------------------- */}
        <AnimatePresence>
          {modalOpen && (
            <>
              <motion.div
                className="uiModalBackdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onMouseDown={() => setModalOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.45)",
                  zIndex: 1050,
                }}
              />

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.18 }}
                style={{
                  position: "fixed",
                  inset: "4vh 4vw",
                  background: "#fff",
                  borderRadius: 14,
                  zIndex: 1060,
                  boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div
                  className="d-flex align-items-center justify-content-between gap-2"
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <div style={{ minWidth: 320 }}>
                    <div className="fw-semibold">AML Screening details</div>
                    <div className="text-muted small d-flex align-items-center gap-2 flex-wrap">
                      {(subjectOptions?.length || 0) > 1 ? (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 320, maxWidth: "78vw" }}
                          value={activeSubjectKey || ""}
                          onChange={(e) => setActiveSubjectKey(e.target.value)}
                        >
                          {!activeSubjectKey ? (
                            <option value="" disabled>
                              Select subject…
                            </option>
                          ) : null}

                          {subjectOptions.map((opt) => (
                            <option key={opt.key} value={opt.key}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="fw-semibold">
                          {activeSubject?.name || "Selected subject"}
                        </span>
                      )}

                      <span>·</span>
                      {badge(`${modalRows.length} rows`, "grey")}
                      {badge(
                        articleSelectionLabel(modalRows.filter(row => selectedIds.has(String(row.id))).length),
                        "grey"
                      )}
                    </div>

                    {/* <div className="text-muted small d-flex align-items-center gap-2 flex-wrap">
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 320, maxWidth: "78vw" }}
                        value={activeSubjectKey || ""}
                        onChange={(e) => setActiveSubjectKey(e.target.value)}
                      >
                        {!activeSubjectKey ? (
                          <option value="" disabled>
                            Select subject…
                          </option>
                        ) : null}

                        {subjectOptions.map((opt) => (
                          <option key={opt.key} value={opt.key}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <span>·</span>
                      {badge(`${modalRows.length} rows`, "grey")}
                      {badge(
                        articleSelectionLabel(modalRows.filter(row => selectedIds.has(String(row.id))).length),
                        "grey"
                      )}
                    </div> */}
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setModalOpen(false)}
                  >
                    Close
                  </button>
                </div>

                <div style={{ padding: 12, overflow: "auto" }}>
                  {!modalRows.length ? (
                    <div className="text-muted">
                      No rows for this subject yet.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table
                        className="table table-sm kycTable"
                        style={{ tableLayout: "fixed" }}
                      >
                        <colgroup>
                          <col style={{ width: 200 }} />
                          <col />
                          <col style={{ width: 150 }} />
                        </colgroup>

                        <thead>
                          <tr>
                            <th style={{ padding: "6px 8px" }}>Subject</th>
                            <th style={{ padding: "6px 8px" }}>
                              Title / Snippet + AI
                            </th>
                            <th
                              style={{ padding: "6px 8px" }}
                              className="text-end"
                            >
                              Action
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {modalRows.map(row => <AdverseMediaArticleRow
                            key={String(row.id)} row={row} result={liveAssessments[String(row.id)]}
                            assessmentPending={isLoading && stage === "assessing" && !assessmentCardReady && candidateRowIdsRef.current.has(String(row.id))}
                            selected={selectedIds.has(String(row.id))} readOnly={readOnly}
                            onToggle={toggleArticleSelection}
                          />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* --- END subject card --- */}
      </div>
    </div>
  );
});

export default KycSectionAdverseMediaWithScreening;
